import {
  BGM_SYSTEM_LABELS,
  CGM_SYSTEM_LABELS,
  PUMP_SYSTEM_LABELS,
  SENSOR_TYPE_LABELS,
  WHEELCHAIRS_AND_SCOOTERS_DEVICE_LABELS,
  prostheticsOptionLabel,
} from "./orderCreation/constants";
import {
  CareBridgeOrder,
  Category,
  ClinicDelivery,
  ClinicInfo,
  CompressionLineItem,
  CompressionOrder,
  CpapLineItem,
  CpapOrder,
  CustomOrthoticsDiagnosis,
  CustomOrthoticsLineItem,
  CustomOrthoticsOrder,
  DepartmentType,
  DiabetesDiagnosis,
  DiabetesLineItem,
  DiabetesOrder,
  Diagnosis,
  MiscellaneousOrder,
  ModernLineItem,
  NebulizerDiagnosis,
  NebulizerLineItem,
  NebulizerOrder,
  OffTheShelfOrthoticsOrder,
  OstomyLineItem,
  OstomyOrder,
  PatientInsuranceInfo,
  PlanOfCare,
  Practice,
  ProstheticsDiagnosis,
  ProstheticsLineItem,
  ProstheticsOrder,
  ShellFormOrder,
  ShippingAddress,
  SignerInfo,
  UrologyLineItem,
  UrologyOrder,
  VentilatorDiagnosis,
  VentilatorLineItem,
  VentilatorOrder,
  WheelchairsAndScootersDiagnosis,
  WheelchairsAndScootersLineItem,
  WheelchairsAndScootersOrder,
  Wound,
  WoundCareLineItem,
  WoundOrder,
  WoundSuctionLineItem,
  WoundSuctionOrder,
  WoundSuctionTreatment,
} from "./__generated__/graphql";
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import {
  customOrthoticsLineItemJointType,
  customOrthoticsLineItemLaterality,
  customOrthoticsLineItemName,
  customOrthoticsLineItemQuantity,
} from "./orderCreation/customOrthoticsLabels";
import {
  formatModernLineItemValuesForSummary,
  titleCase,
} from "./utils/formatting";
import {
  generateCategoryDiagnosisTypeDisplays,
  generatePlanOfCareHeadersAndDisplaysForCategory,
} from "./modernForm/utils";
import {
  getOxygenPortableType,
  getOxygenStationaryType,
} from "./utils/oxygenSwo";
import {
  getTrapezeSwoDisplayOptions,
  getTrapezeWeightCapacityOptionName,
} from "./utils/trapezeSwo";
import {
  isFilledWoundSuctionTreatment,
  nebulizerMaskMeasurements,
  nebulizerMedicationNames,
} from "./orderCreation/util";

import { SHELL_FORM_CONFIG } from "./orderCreation/shellFormConfig";
import { getBariatricWheelchairItemOverride } from "./utils/wheelchairUtils";
import { getSwoCategoryHeader } from "./utils/swoCategoryHeader";

export enum ProductSubCategory {
  ArmSleeve = "ARM_SLEEVE",
  CompressionBra = "COMPRESSION_BRA",
  Stocking = "STOCKING",
  Wrap = "WRAP",
}

export const mapDrainage = (abbreviation: string): string | undefined => {
  const mapping = new Map<string, string>([
    ["N", "None"],
    ["L", "Low"],
    ["M", "Moderate"],
    ["H", "Heavy"],
  ]);

  return mapping.get(abbreviation);
};

// Only what the PDF prints: the order queries that feed it skip the ids and
// timestamps.
type SwoWoundOrder = Omit<WoundOrder, "wounds"> & {
  wounds: Omit<Wound, "index">[];
};
type SwoCpapOrder = Omit<
  CpapOrder,
  "id" | "createdAt" | "updatedAt" | "cpapLineItems"
> & {
  // `id` only feeds a React key that also carries the row index.
  cpapLineItems: (Omit<CpapLineItem, "id" | "createdAt" | "updatedAt"> &
    Partial<Pick<CpapLineItem, "id">>)[];
};

const formatSignatureTime = (dateString: string) => {
  const parts = dateString.split("T");
  return parts[0] + " " + parts[1].split(".")[0];
};

const getMergedWoundCareLineItems = (
  woundOrder: SwoWoundOrder,
): (Omit<WoundCareLineItem, "id" | "woundId" | "calculatedSizeData"> & {
  wounds: number[];
  totalQuantity: number | null;
})[] => {
  // calculatedSizeData carries per-wound woundIds, which differ between
  // sibling per-wound rows, so it must be excluded from the uniqueness
  // check below or sibling dressings never merge.
  const woundCareLineItemsWithoutIds = woundOrder.woundCareLineItems.map(
    (item) => {
      const { id, woundId, calculatedSizeData, ...lineItem } = item;
      return lineItem;
    },
  );

  const uniqueWoundCareLineItems = woundCareLineItemsWithoutIds.filter(
    (v, i, a) =>
      a.findIndex((v2) => JSON.stringify(v2) === JSON.stringify(v)) === i,
  );

  const woundCareLineItemsWithWounds: (Omit<
    WoundCareLineItem,
    "id" | "woundId" | "calculatedSizeData"
  > & { wounds: number[]; totalQuantity: number | null })[] = [];
  uniqueWoundCareLineItems.forEach((item) => {
    const woundIds: number[] = [];

    // quantity and calculatedSizeData.quantity: both are the calculator's
    // total for the line item, duplicated onto every sibling row (per-wound
    // or order-wide) that shares this merge key. take one value, don't sum.

    let quantity: number | null = null;
    let calculatedQuantity: number | null = null;
    woundOrder.woundCareLineItems.forEach((wli) => {
      const { id, woundId, calculatedSizeData, ...lineItem } = wli;
      if (JSON.stringify(lineItem) !== JSON.stringify(item)) return;

      if (woundId != null) {
        woundIds.push(woundId);
      }
      if (wli.quantity) {
        quantity = Number(wli.quantity);
      } else if (
        calculatedQuantity == null &&
        calculatedSizeData?.quantity != null
      ) {
        calculatedQuantity = calculatedSizeData.quantity;
      }
    });

    const totalQuantity = quantity ?? calculatedQuantity;

    woundCareLineItemsWithWounds.push({
      ...item,
      wounds: woundIds,
      totalQuantity,
    });
  });

  return woundCareLineItemsWithWounds;
};

/**
 * Returns prescription/specification lines for a CPAP line item based on
 * the product name and the order-level settings.
 */
const getCpapLineItemSpecs = (
  productName: string,
  order: SwoCpapOrder,
): string[] => {
  const specs: string[] = [];
  const name = productName.trim();

  // Machine items — show relevant pressure settings
  if (name === "CPAP") {
    if (order.cpapPressure) specs.push(`Pressure: ${order.cpapPressure} cmH2O`);
  } else if (name === "Auto CPAP") {
    if (order.autoCpapPressureMin)
      specs.push(`Pressure min: ${order.autoCpapPressureMin} cmH2O`);
    if (order.autoCpapPressureMax)
      specs.push(`Pressure max: ${order.autoCpapPressureMax} cmH2O`);
    if (order.autoCpapExpiratoryRelief)
      specs.push(`Expiratory relief: ${order.autoCpapExpiratoryRelief}`);
    if (order.autoCpapRampTime)
      specs.push(`Ramp time: ${order.autoCpapRampTime}`);
    if (order.autoCpapStartingPressure)
      specs.push(`Starting pressure: ${order.autoCpapStartingPressure} cmH2O`);
  } else if (name === "Bi-Level PAP") {
    if (order.bilevelIpap) specs.push(`IPAP: ${order.bilevelIpap} cmH2O`);
    if (order.bilevelEpap) specs.push(`EPAP: ${order.bilevelEpap} cmH2O`);
  } else if (name === "Auto Bi-Level PAP") {
    if (order.autoBilevelPressureSupport)
      specs.push(`Pressure support: ${order.autoBilevelPressureSupport} cmH2O`);
    if (order.autoBilevelMaxIpap)
      specs.push(`Max IPAP: ${order.autoBilevelMaxIpap} cmH2O`);
    if (order.autoBilevelMinEpap)
      specs.push(`Min EPAP: ${order.autoBilevelMinEpap} cmH2O`);
  } else if (name === "BiPAP ST") {
    if (order.bipapStMaxIpap)
      specs.push(`Max IPAP: ${order.bipapStMaxIpap} cmH2O`);
    if (order.bipapStMinEpap)
      specs.push(`Min EPAP: ${order.bipapStMinEpap} cmH2O`);
    if (order.bipapStPressureSupportMin || order.bipapStPressureSupportMax)
      specs.push(
        `Pressure support: ${order.bipapStPressureSupportMin ?? "–"}–${order.bipapStPressureSupportMax ?? "–"} cmH2O`,
      );
    if (order.bipapStBackupRate)
      specs.push(`Backup rate: ${order.bipapStBackupRate}`);
    if (order.bipapStRampTime)
      specs.push(`Ramp time: ${order.bipapStRampTime}`);
  }

  // Machines get nocturnal oxygen if present
  const machineNames = [
    "CPAP",
    "Auto CPAP",
    "Bi-Level PAP",
    "Auto Bi-Level PAP",
    "BiPAP ST",
  ];
  if (
    machineNames.includes(name) &&
    order.usesOxygen &&
    order.nocturnalOxygen
  ) {
    specs.push(`Nocturnal oxygen: ${order.nocturnalOxygen} LPM`);
  }

  // Mask items — show nose measurements
  const maskNames = ["Full Face", "Nasal Mask", "Nasal Pillow"];
  if (maskNames.includes(name)) {
    if (order.noseToChin != null)
      specs.push(`Nose to chin: ${order.noseToChin} cm`);
    if (order.noseWidth != null)
      specs.push(`Nose width: ${order.noseWidth} cm`);
    if (order.noseLength != null)
      specs.push(`Nose length: ${order.noseLength} cm`);
    if (order.existingSizeAndModel)
      specs.push(`Existing size/model: ${order.existingSizeAndModel}`);
  }

  return specs;
};

interface Props {
  orderNumber: string;
  patientName?: string | null;
  phoneNumber?: string | null;
  secondaryPhoneNumber?: string | null;
  shippingAddress: Omit<ShippingAddress, "id" | "createdAt" | "updatedAt">;
  woundOrder: SwoWoundOrder;
  compressionOrder: Pick<
    CompressionOrder,
    "compressionPlanOfCare" | "diagnoses"
  > & {
    compressionLineItems: Omit<
      CompressionLineItem,
      "id" | "createdAt" | "updatedAt"
    >[];
  };
  ostomyOrder: Omit<
    OstomyOrder,
    "id" | "createdAt" | "updatedAt" | "ostomyLineItems"
  > & {
    ostomyLineItems: Omit<OstomyLineItem, "id" | "createdAt" | "updatedAt">[];
  };
  urologyOrder: Omit<
    UrologyOrder,
    "id" | "createdAt" | "updatedAt" | "urologyLineItems"
  > & {
    urologyLineItems: Omit<UrologyLineItem, "id" | "createdAt" | "updatedAt">[];
  };
  cpapOrder: SwoCpapOrder | undefined | null;
  // No `Omit<..., "id">` needed here, unlike the forms above: this order's SDL id
  // is nullable, so it is already optional on the generated type.
  offTheShelfOrthoticsOrder: OffTheShelfOrthoticsOrder | undefined | null;
  customOrthoticsOrder:
    | (Pick<
        CustomOrthoticsOrder,
        | "isLifetimeLengthOfNeed"
        | "lengthOfNeedInMonths"
        | "patientWeightLbs"
        | "patientAgeYears"
        | "shoeSize"
        | "shoeWidth"
        | "headNeckSpineSpecifications"
        | "upperLimbSpecifications"
        | "lowerLimbSpecifications"
        | "kneeBracesSpecifications"
        | "feetSpecifications"
        | "preferredDmeCompany"
      > & {
        customOrthoticsDiagnoses: Pick<
          CustomOrthoticsDiagnosis,
          "diagnosis" | "icd10Code"
        >[];
        customOrthoticsLineItems: Pick<
          CustomOrthoticsLineItem,
          | "id"
          | "region"
          | "productName"
          | "bodyParts"
          | "laterality"
          | "jointType"
          | "quantity"
          | "isMaximumAllowedQuantity"
          | "hcpcsCode"
        >[];
      })
    | undefined
    | null;
  // Picked rather than Omit<..., "id">: no query selects the order-level id (an
  // order without wheelchairs & scooters data reads back as an empty sentinel
  // whose id is null), and the SWO renders none of the medical-necessity
  // answers, so the queries that feed it do not select them either.
  wheelchairsAndScootersOrder:
    | (Pick<
        WheelchairsAndScootersOrder,
        | "deviceType"
        | "otherDeviceDescription"
        | "isLifetimeLengthOfNeed"
        | "lengthOfNeedInMonths"
        | "patientHeightFeet"
        | "patientHeightInches"
        | "patientWeightLbs"
        | "preferredDmeCompany"
      > & {
        wheelchairsAndScootersDiagnoses: Pick<
          WheelchairsAndScootersDiagnosis,
          "diagnosis" | "icd10Code"
        >[];
        wheelchairsAndScootersLineItems: Pick<
          WheelchairsAndScootersLineItem,
          "id" | "productName" | "hcpcsCode" | "size" | "quantity"
        >[];
      })
    | undefined
    | null;
  // One entry per shell form the provider filled in. Unlike the per-form prop
  // this replaces, the order-level `id` is NOT omitted: there is no empty
  // sentinel any more, so every entry has a real id and queries select it.
  //
  // Required (not optional) on purpose -- a caller that forgets to pass it is a
  // compile error rather than a PDF that silently renders no shell form section.
  shellFormOrders: ShellFormOrder[] | undefined | null;
  // Picked rather than Omit<..., "id">: no query selects the order-level id (an
  // order without nebulizer data reads back as an empty sentinel whose id is
  // null), and the SWO renders none of the medical-necessity attestations, so
  // the queries that feed it do not select them either. Listing exactly what
  // this document reads keeps every caller assignable.
  nebulizerOrder:
    | (Pick<
        NebulizerOrder,
        | "medication"
        | "otherMedication"
        | "usesSterileSalineOrWater"
        | "dosingInstructions"
        | "isLifetimeLengthOfNeed"
        | "lengthOfNeedInMonths"
        | "administrationSetType"
        | "noseToChinCm"
        | "tracheostomyInnerDiameterMm"
        | "tracheostomyOuterDiameterMm"
        | "tracheostomyLengthMm"
      > & {
        nebulizerDiagnoses: Pick<
          NebulizerDiagnosis,
          "diagnosis" | "icd10Code"
        >[];
        nebulizerLineItems: Pick<
          NebulizerLineItem,
          "id" | "productName" | "hcpcsCode" | "size" | "quantity"
        >[];
      })
    | undefined
    | null;
  // Picked for the same reason as nebulizerOrder above: no query selects the
  // order-level id, and this document renders none of the necessity answers. The
  // interface measurements are absent too -- they reach this page as the line
  // item's `size`, built at insert time.
  ventilatorOrder:
    | (Pick<
        VentilatorOrder,
        | "ventilatorType"
        | "interfaceType"
        | "isLifetimeLengthOfNeed"
        | "lengthOfNeedInMonths"
        | "preferredDmeCompany"
      > & {
        ventilatorDiagnoses: Pick<
          VentilatorDiagnosis,
          "diagnosis" | "icd10Code"
        >[];
        ventilatorLineItems: Pick<
          VentilatorLineItem,
          "id" | "productName" | "hcpcsCode" | "size" | "quantity"
        >[];
      })
    | undefined
    | null;
  // Picked for the same reason as nebulizerOrder above: no query selects the
  // order-level id, and the only medical-necessity answer this document renders
  // is the inpatient setting, so the queries that feed it select nothing else
  // from the interview.
  //
  // The wounds this section prints come from `woundOrder`, which is where they
  // live -- but they are printed HERE because the Wound Care section is gated on
  // wound-care dressings, so a Wound Suction order's wounds appear nowhere else
  // in this document. A written order that prescribes a pump without saying
  // which wound it treats is not one a DME can fulfil.
  woundSuctionOrder:
    | (Pick<
        WoundSuctionOrder,
        "isTherapyInitiatedInpatient" | "preferredDmeCompany"
      > & {
        woundSuctionTreatments: Pick<
          WoundSuctionTreatment,
          "id" | "selectedWoundIndices" | "durationOfUse" | "frequencyOfChange"
        >[];
        woundSuctionLineItems: Pick<
          WoundSuctionLineItem,
          "id" | "productName" | "hcpcsCode" | "quantity"
        >[];
      })
    | undefined
    | null;
  // Picked for the same reason as nebulizerOrder above. The systems and their
  // initial-order answers are fulfilment facts, not coverage ones: they decide
  // whether the hardware ships and which sensor duration goes in the box.
  diabetesOrder:
    | (Pick<
        DiabetesOrder,
        | "bgmSystem"
        | "bgmIsInitialOrder"
        | "cgmSystem"
        | "cgmIsInitialOrder"
        | "cgmSensorType"
        | "pumpSystem"
        | "pumpIsInitialOrder"
        | "isLifetimeLengthOfNeed"
        | "lengthOfNeedInMonths"
        | "preferredDmeCompany"
      > & {
        diabetesDiagnoses: Pick<DiabetesDiagnosis, "diagnosis" | "icd10Code">[];
        diabetesLineItems: Pick<
          DiabetesLineItem,
          "id" | "productName" | "hcpcsCode" | "size" | "quantity"
        >[];
      })
    | undefined
    | null;
  // Picked for the same reason as nebulizerOrder above. The fitting details --
  // sites, circumferences, attachment mechanisms, shoe and bra sizes -- are
  // what the DME builds to, so they print alongside the items they belong to.
  prostheticsOrder:
    | (Pick<
        ProstheticsOrder,
        | "functionalLevel"
        | "isLifetimeLengthOfNeed"
        | "lengthOfNeedInMonths"
        | "preferredDmeCompany"
        | "mastectomyAmputationSite"
        | "mastectomyBandSize"
        | "mastectomyCupSize"
        | "lowerLimbRightAmputationSite"
        | "lowerLimbRightCircumference"
        | "lowerLimbRightAttachmentMechanism"
        | "lowerLimbLeftAmputationSite"
        | "lowerLimbLeftCircumference"
        | "lowerLimbLeftAttachmentMechanism"
        | "toeFillersWeight"
        | "toeFillersAge"
        | "toeFillersShoeSize"
        | "toeFillersShoeWidth"
        | "toeFillersLeftToesMissing"
        | "toeFillersRightToesMissing"
      > & {
        prostheticsDiagnoses: Pick<
          ProstheticsDiagnosis,
          "diagnosis" | "icd10Code"
        >[];
        prostheticsLineItems: Pick<
          ProstheticsLineItem,
          | "id"
          | "subcategory"
          | "productName"
          | "hcpcsCode"
          | "size"
          | "quantity"
          | "remarks"
        >[];
      })
    | undefined
    | null;
  careBridgeOrder: CareBridgeOrder | undefined | null;
  miscellaneousOrder: MiscellaneousOrder | undefined | null;
  signerInfo?:
    | (Omit<SignerInfo, "practices"> & {
        practices: Omit<Practice, "validationStatus">[];
      })
    | null;
  clinicInfo?: ClinicInfo | null;
  clinicDelivery?:
    | (Omit<ClinicDelivery, "address"> & {
        address: Omit<ShippingAddress, "id" | "createdAt" | "updatedAt">;
      })
    | null;
  createdAt: string;
  patientDob?: string | null;
  gender?: string | null;
  insuranceInfo?: Omit<
    PatientInsuranceInfo,
    "id" | "createdAt" | "updatedAt"
  > | null;
  logoSrc?: string;
  automaticRoutedOrder?: boolean;
  departmentCategories: Category[] | null;
  planOfCares: PlanOfCare[];
  diagnoses: Diagnosis[];
  modernLineItems: ModernLineItem[];
}

export function StandardWrittenOrderPDF(props: Props) {
  const mergedWoundCareLineItems = getMergedWoundCareLineItems(
    props.woundOrder,
  );
  // Half-filled Treatment plan lines are a real state to store (the design ships
  // two blank rows), but not one to print -- an empty row on a written order
  // reads as a product the DME failed to supply.
  const woundSuctionTreatments = (
    props.woundSuctionOrder?.woundSuctionTreatments ?? []
  ).filter(isFilledWoundSuctionTreatment);
  // The positions on a Treatment plan line index this array. They are stored as
  // `order_wounds` ids and turned back into positions on read, so they line up
  // with the order the wounds print in below.
  const woundSuctionWounds = props.woundOrder?.wounds ?? [];

  const styles = StyleSheet.create({
    page: { margin: "30px", paddingBottom: "60px" },
    logoImage: { width: "150px" },
    text: { color: "black", fontSize: "8px" },
    debugText: { color: "gray", fontSize: "6px" },
    aboutText: { fontSize: "10px" },
    subheaderText: { fontSize: "12px", marginTop: "5px", marginBottom: "5px" },
    subheaderTextRight: {
      fontSize: "12px",
      marginTop: "5px",
      marginBottom: "5px",
      textAlign: "right",
    },
    headerRight: { fontSize: "18px", marginTop: "10px", textAlign: "right" },
    sectionHeader: {
      fontSize: "16px",
      marginTop: "10px",
      marginBottom: "10px",
      color: "black",
      fontWeight: "bold",
    },
    marginBottom: { marginBottom: "10px" },
    marginTop: { marginTop: "7px" },
    woundSection: {
      width: "30%",
      float: "left",
      border: "1px solid black",
      borderRadius: "4px",
      padding: "10px",
    },
    flex: { display: "flex", gap: "0px" },
    row: { flexDirection: "row", alignItems: "stretch", marginTop: "-1px" },
    halfWidth: { width: "270px" },
    fullWidth: { width: "540px" },
    header2: {
      fontWeight: "bold",
      fontSize: "14px",
      marginBottom: "5px",
      marginTop: "5px",
    },
    borderBottom: { borderBottom: "1px solid gray" },
    bold: { fontFamily: "Helvetica-Bold" },
    inline: { display: "flex", flexDirection: "row" },
    spaceAfter: { marginRight: "3px" },
    labelCell: {
      fontFamily: "Helvetica-Bold",
      backgroundColor: "#F0F0F0",
      border: "1px solid #C0C0C0",
      fontSize: "8px",
      width: "100px",
      padding: "6px",
      margin: "0px",
    },
    headerCell: {
      fontFamily: "Helvetica-Bold",
      backgroundColor: "#F0F0F0",
      border: "1px solid #C0C0C0",
      fontSize: "8px",
      padding: "6px",
      width: "100px",
      margin: "0px",
      marginLeft: "-1px",
    },
    infoCell: {
      border: "1px solid #C0C0C0",
      fontSize: "8px",
      width: "170px",
      padding: "6px",
      margin: "0px",
      marginLeft: "-1px",
    },
    outlineBox: {
      border: "1px solid #C0C0C0",
      width: "220px",
      height: "30px",
      marginBottom: "10px",
      marginRight: "10px",
    },
  });

  const { compressionPlanOfCare, compressionLineItems } =
    props.compressionOrder;
  const compressionLineItemMap = new Map<
    ProductSubCategory,
    Omit<CompressionLineItem, "id" | "createdAt" | "updatedAt">[]
  >();
  compressionLineItems.forEach((cli) => {
    const subCategory = cli.subCategory || ProductSubCategory.Wrap; // default to Wrap, although it should never be null
    if (!compressionLineItemMap.has(subCategory)) {
      compressionLineItemMap.set(subCategory, []);
    }
    compressionLineItemMap.get(subCategory)?.push(cli);
  });
  const wrapStockingCompressionItems = [
    ...(compressionLineItemMap.get(ProductSubCategory.Stocking) ?? []),
    ...(compressionLineItemMap.get(ProductSubCategory.Wrap) ?? []),
  ];

  const optionalInfoRows = (rows: [string, string | null | undefined][]) =>
    rows
      .filter(([, value]) => value)
      .map(([label, value]) => (
        <View style={styles.row} key={label}>
          <View style={styles.labelCell}>
            <Text>{label}</Text>
          </View>
          <View style={styles.infoCell}>
            <Text>{value}</Text>
          </View>
        </View>
      ));

  const customOrthoticsSpecificationRows = optionalInfoRows([
    [
      "Head / Neck / Spine",
      props.customOrthoticsOrder?.headNeckSpineSpecifications,
    ],
    ["Upper Limb", props.customOrthoticsOrder?.upperLimbSpecifications],
    ["Lower Limb", props.customOrthoticsOrder?.lowerLimbSpecifications],
    ["Knee Braces", props.customOrthoticsOrder?.kneeBracesSpecifications],
    ["Diabetic Feet", props.customOrthoticsOrder?.feetSpecifications],
  ]);

  // A row per system, because each carries its own initial-order answer.
  const diabetesInitialOrderNote = (
    isInitialOrder: boolean | null | undefined,
    resupplyOnly: string,
  ): string | null =>
    isInitialOrder == null
      ? null
      : isInitialOrder
        ? "initial order"
        : resupplyOnly;

  const diabetesSystemRows = optionalInfoRows([
    [
      "Blood Glucose Monitor",
      props.diabetesOrder?.bgmSystem
        ? [
            BGM_SYSTEM_LABELS[props.diabetesOrder.bgmSystem],
            diabetesInitialOrderNote(
              props.diabetesOrder.bgmIsInitialOrder,
              "accessories only",
            ),
          ]
            .filter(Boolean)
            .join(", ")
        : null,
    ],
    [
      "Continuous Glucose Monitor",
      props.diabetesOrder?.cgmSystem
        ? [
            CGM_SYSTEM_LABELS[props.diabetesOrder.cgmSystem],
            props.diabetesOrder.cgmSensorType
              ? SENSOR_TYPE_LABELS[props.diabetesOrder.cgmSensorType]
              : null,
            diabetesInitialOrderNote(
              props.diabetesOrder.cgmIsInitialOrder,
              "sensors only",
            ),
          ]
            .filter(Boolean)
            .join(", ")
        : null,
    ],
    [
      "Insulin Pump",
      props.diabetesOrder?.pumpSystem
        ? [
            PUMP_SYSTEM_LABELS[props.diabetesOrder.pumpSystem],
            diabetesInitialOrderNote(
              props.diabetesOrder.pumpIsInitialOrder,
              "monthly supplies only",
            ),
          ]
            .filter(Boolean)
            .join(", ")
        : null,
    ],
  ]);

  const prostheticsSectionHeadingStyle = {
    ...styles.header2,
    marginTop: "14px",
  };
  const prostheticsSubLabelStyle = {
    ...styles.subheaderText,
    fontSize: "10px",
  };
  const prostheticsItemsLabelStyle = {
    ...prostheticsSubLabelStyle,
    marginTop: "6px",
  };

  // Roughly a page's worth of table rows. See the `wrap` prop below.
  const PROSTHETICS_UNBREAKABLE_ROW_LIMIT = 20;

  const prostheticsOrderedItems = (
    props.prostheticsOrder?.prostheticsLineItems ?? []
  ).filter((li) => li.productName);

  const prostheticsSectionedSubcategories = [
    "MASTECTOMY",
    "LOWER_LIMB",
    "TOE_FILLERS",
  ];

  type ProstheticsOrderedItem = (typeof prostheticsOrderedItems)[number];

  const prostheticsColumns = (capturesItemDetails: boolean) => [
    {
      header: "Item",
      width: capturesItemDetails ? "230px" : "240px",
      value: (li: ProstheticsOrderedItem) => li.productName,
    },
    ...(capturesItemDetails
      ? []
      : [
          {
            header: "HCPCS",
            width: "90px",
            value: (li: ProstheticsOrderedItem) => li.hcpcsCode,
          },
        ]),
    {
      // Catalog rows carry the leg and part (e.g. "Right, Below knee").
      header: capturesItemDetails ? "Size" : "Specification",
      width: capturesItemDetails ? "100px" : "130px",
      value: (li: ProstheticsOrderedItem) => li.size,
    },
    {
      header: "Qty",
      width: capturesItemDetails ? "60px" : "80px",
      value: (li: ProstheticsOrderedItem) =>
        li.quantity != null ? String(li.quantity) : null,
    },
    ...(capturesItemDetails
      ? [
          {
            header: "Remarks",
            width: "150px",
            value: (li: ProstheticsOrderedItem) => li.remarks,
          },
        ]
      : []),
  ];

  const prostheticsLabel = (value: string | null | undefined) =>
    value ? prostheticsOptionLabel(value) : null;

  const prostheticsLengthOfNeed = props.prostheticsOrder?.isLifetimeLengthOfNeed
    ? "Lifetime"
    : props.prostheticsOrder?.lengthOfNeedInMonths
      ? `${props.prostheticsOrder.lengthOfNeedInMonths} months`
      : null;

  const prostheticsDiagnoses = (
    props.prostheticsOrder?.prostheticsDiagnoses ?? []
  ).filter((diag) => diag.icd10Code || diag.diagnosis);

  const prostheticsSections = [
    {
      heading: "Mastectomy",
      capturesItemDetails: false,
      rows: optionalInfoRows([
        [
          "Amputation site",
          prostheticsLabel(props.prostheticsOrder?.mastectomyAmputationSite),
        ],
        ["Band size", props.prostheticsOrder?.mastectomyBandSize],
        ["Cup size", props.prostheticsOrder?.mastectomyCupSize],
      ]),
      lineItems: prostheticsOrderedItems.filter(
        (li) => li.subcategory === "MASTECTOMY",
      ),
    },
    {
      heading: "Lower Limb",
      capturesItemDetails: false,
      rows: optionalInfoRows([
        [
          "Right leg amputation site",
          prostheticsLabel(
            props.prostheticsOrder?.lowerLimbRightAmputationSite,
          ),
        ],
        [
          "Right leg circumference",
          prostheticsLabel(props.prostheticsOrder?.lowerLimbRightCircumference),
        ],
        [
          "Right leg attachment mechanism",
          prostheticsLabel(
            props.prostheticsOrder?.lowerLimbRightAttachmentMechanism,
          ),
        ],
        [
          "Left leg amputation site",
          prostheticsLabel(props.prostheticsOrder?.lowerLimbLeftAmputationSite),
        ],
        [
          "Left leg circumference",
          prostheticsLabel(props.prostheticsOrder?.lowerLimbLeftCircumference),
        ],
        [
          "Left leg attachment mechanism",
          prostheticsLabel(
            props.prostheticsOrder?.lowerLimbLeftAttachmentMechanism,
          ),
        ],
      ]),
      lineItems: prostheticsOrderedItems.filter(
        (li) => li.subcategory === "LOWER_LIMB",
      ),
    },
    {
      heading: "Toe Fillers",
      capturesItemDetails: false,
      rows: optionalInfoRows([
        ["Weight", props.prostheticsOrder?.toeFillersWeight],
        ["Age", props.prostheticsOrder?.toeFillersAge],
        ["Shoe size", props.prostheticsOrder?.toeFillersShoeSize],
        ["Shoe width", props.prostheticsOrder?.toeFillersShoeWidth],
        [
          "Left toes missing",
          props.prostheticsOrder?.toeFillersLeftToesMissing,
        ],
        [
          "Right toes missing",
          props.prostheticsOrder?.toeFillersRightToesMissing,
        ],
      ]),
      lineItems: prostheticsOrderedItems.filter(
        (li) => li.subcategory === "TOE_FILLERS",
      ),
    },
    {
      heading: "Other",
      capturesItemDetails: true,
      rows: [],
      lineItems: prostheticsOrderedItems.filter(
        (li) => !prostheticsSectionedSubcategories.includes(li.subcategory),
      ),
    },
  ].filter(
    (section) => section.rows.length > 0 || section.lineItems.length > 0,
  );

  const diagnosisTypeToCategory = new Map<number, number>();
  props.departmentCategories?.forEach((category) => {
    category.categoryDiagnosisTypes?.forEach((categoryDiagnosisType) => {
      diagnosisTypeToCategory.set(categoryDiagnosisType.id, category.id);
    });
  });

  const activeCategoryIds = [
    ...new Set(
      [
        ...props.planOfCares.map((poc) => poc.categoryId),
        ...props.diagnoses.map((d) =>
          diagnosisTypeToCategory.get(d.categoryDiagnosisTypeId),
        ),
        ...props.modernLineItems.map((li) => li.categoryId),
      ].filter((id) => id !== undefined),
    ),
  ];

  const ModernOrderSummary = activeCategoryIds.map((categoryId) => {
    if (!props.departmentCategories) {
      return null;
    }

    const category = props.departmentCategories.find(
      (c) => c.id === categoryId,
    );
    if (!category) {
      return null;
    }

    // Check if this is an Oxygen category - render custom layout
    // Note: "OXYGEN" is the identifier value from the database Category table
    if (category.identifier === "OXYGEN") {
      // Get diagnosis information
      const diagnoses = props.diagnoses.filter((d) =>
        category.categoryDiagnosisTypes.some(
          (cdt) => cdt.id === d.categoryDiagnosisTypeId,
        ),
      );

      // Get plan of care data for oxygen
      const planOfCare = props.planOfCares.find(
        (poc) => poc.categoryId === category.id,
      );

      // Get line items for oxygen
      const lineItems = props.modernLineItems.filter(
        (li) => li.categoryId === categoryId,
      );

      return (
        <View key={category.id}>
          <Text style={styles.sectionHeader}>
            Order Detail: {category.displayName}
          </Text>
          <View style={styles.borderBottom} />

          {/* Diagnosis Section */}
          <Text style={styles.subheaderText}>Diagnosis:</Text>
          <View style={styles.flex}>
            <View style={styles.row}>
              <View style={{ ...styles.labelCell, width: 200 }}>
                <Text>Diagnosis/Symptoms</Text>
              </View>
              <View style={{ ...styles.infoCell, width: 340 }}>
                {diagnoses.map((diagnosis, index) => {
                  const diagnosisDisplay = category.categoryDiagnosisTypes
                    .flatMap((cdt) =>
                      generateCategoryDiagnosisTypeDisplays(
                        [diagnosis],
                        cdt,
                        category.defaultIcd10Descriptions || [],
                      ),
                    )
                    .flat();

                  return diagnosisDisplay[0] ? (
                    <View key={index} style={{ flexDirection: "row" }}>
                      <Text>{index + 1}. </Text>
                      <Text>{diagnosisDisplay[0]}</Text>
                    </View>
                  ) : null;
                })}
              </View>
            </View>
          </View>

          {/* Prescription Section */}
          <Text style={styles.subheaderText}>Prescription:</Text>
          <View style={styles.flex}>
            <View style={styles.row}>
              <View style={{ ...styles.headerCell, width: 270 }}>
                <Text>Prescription</Text>
              </View>
              <View style={{ ...styles.headerCell, width: 270 }}>
                <Text>Length of Need</Text>
              </View>
            </View>
            <View style={styles.row}>
              <View style={{ ...styles.infoCell, width: 270 }}>
                <Text>
                  {planOfCare?.oxygenPrescription
                    ? /^\d+(\.\d+)?$/.test(
                        String(planOfCare.oxygenPrescription),
                      )
                      ? `${planOfCare.oxygenPrescription} L/min`
                      : String(planOfCare.oxygenPrescription)
                    : ""}
                </Text>
              </View>
              <View style={{ ...styles.infoCell, width: 270 }}>
                <Text>
                  {planOfCare?.oxygenLengthOfNeedType === "Lifetime"
                    ? "Lifetime"
                    : planOfCare?.oxygenLengthOfNeedMonths
                      ? `${planOfCare.oxygenLengthOfNeedMonths} months`
                      : ""}
                </Text>
              </View>
            </View>
          </View>

          {/* Ordered Items Section */}
          <Text style={styles.subheaderText}>Ordered Items:</Text>
          <View style={styles.flex}>
            <View style={styles.row}>
              <View style={{ ...styles.headerCell, width: 170 }}>
                <Text>Product Specs</Text>
              </View>
              <View style={{ ...styles.headerCell, width: 370 }}>
                <Text>Additional Info / Accessories</Text>
              </View>
            </View>

            {/* Usage */}
            <View style={styles.row}>
              <View style={{ ...styles.infoCell, width: 170 }}>
                <Text>Usage</Text>
              </View>
              <View style={{ ...styles.infoCell, width: 370 }}>
                <Text>
                  {lineItems
                    .map((li) => {
                      if (
                        li.formItemName === "Continuous (24 hours)" ||
                        li.formItemName === "Continuous"
                      )
                        return "Continuous (24 hours, Same rx day and night)";
                      if (li.formItemName === "Nocturnally")
                        return "Nocturnally (Hours of sleep only)";
                      if (li.formItemName === "With Exercise")
                        return "With Exercise (With Activity)";
                      return li.formItemName;
                    })
                    .join(" / ")}
                </Text>
              </View>
            </View>

            {/* Stationary type */}
            {/* Note: formItemName values ("Nocturnally", "Continuous (24 hours)", "With Exercise", and legacy "Continuous") are from the database.
                If these values change in the database, this logic will break. See utils/oxygenSwo.ts. */}
            <View style={styles.row}>
              <View style={{ ...styles.infoCell, width: 170 }}>
                <Text>Stationary type</Text>
              </View>
              <View style={{ ...styles.infoCell, width: 370 }}>
                <Text>{getOxygenStationaryType(lineItems)}</Text>
              </View>
            </View>

            {/* Portable type */}
            {/* Suppressed when the patient is explicitly not mobile in the home —
                Continuous implies portable equipment, but a non-mobile
                patient can't receive it. See utils/oxygenSwo.ts. */}
            <View style={styles.row}>
              <View style={{ ...styles.infoCell, width: 170 }}>
                <Text>Portable type</Text>
              </View>
              <View style={{ ...styles.infoCell, width: 370 }}>
                <Text>
                  {getOxygenPortableType(
                    lineItems,
                    planOfCare?.oxygenPatientMobileInHome,
                    planOfCare?.oxygenPortableType,
                  )}
                </Text>
              </View>
            </View>

            {/* Route */}
            <View style={styles.row}>
              <View style={{ ...styles.infoCell, width: 170 }}>
                <Text>Route</Text>
              </View>
              <View style={{ ...styles.infoCell, width: 370 }}>
                <Text>{planOfCare?.oxygenMethod || ""}</Text>
              </View>
            </View>

            {/* Additional Specifications */}
            <View style={styles.row}>
              <View style={{ ...styles.infoCell, width: 170 }}>
                <Text>Additional Specifications</Text>
              </View>
              <View style={{ ...styles.infoCell, width: 370 }}>
                <Text>
                  {[
                    planOfCare?.oxygenConservingDeviceSetAt != null
                      ? `Oxygen Conserving Device Set at ${planOfCare.oxygenConservingDeviceSetAt}`
                      : null,
                    planOfCare?.oxygenAdditionalSpecifications || null,
                  ]
                    .filter(Boolean)
                    .join("\n") || ""}
                </Text>
              </View>
            </View>
          </View>
        </View>
      );
    }

    // Check if this is a Wheelchair category - render custom layout
    if (category.identifier === "WHEELCHAIR") {
      const wheelchairLineItems = props.modernLineItems.filter(
        (li) => li.categoryId === categoryId,
      );

      const getVariantIds = (subcategoryName: string): Set<number> =>
        new Set(
          category.subcategories
            ?.find((s) => s.name === subcategoryName)
            ?.formItems.flatMap((fi) => fi.variants.map((v) => v.id)) ?? [],
        );

      const defaultVariantIds = getVariantIds("Default");
      const optionsVariantIds = getVariantIds("Wheelchair Options");
      const cushionsVariantIds = getVariantIds("Cushions");

      const defaultSubcategoryFormItems =
        category.subcategories?.find((s) => s.name === "Default")?.formItems ??
        [];

      // Map: optionId → { groupName, optionName }
      const optionIdToInfo = new Map<
        number,
        { groupName: string; optionName: string }
      >();
      // Map: variantId → selectedOptionIds
      const variantIdToOptionIds = new Map<number, number[]>();
      defaultSubcategoryFormItems.forEach((fi) => {
        fi.optionGroups.forEach((group) => {
          group.options.forEach((opt) => {
            optionIdToInfo.set(opt.id, {
              groupName: group.name,
              optionName: opt.name,
            });
          });
        });
        fi.variants.forEach((v) => {
          variantIdToOptionIds.set(v.id, v.formItemOptionIds);
        });
      });

      const wheelchairTypeItems = wheelchairLineItems.filter((li) =>
        defaultVariantIds.has(li.formItemVariantId),
      );
      const optionsItems = wheelchairLineItems.filter((li) =>
        optionsVariantIds.has(li.formItemVariantId),
      );
      const cushionsItems = wheelchairLineItems.filter((li) =>
        cushionsVariantIds.has(li.formItemVariantId),
      );

      const wheelchairDiagnoses = props.diagnoses.filter((d) =>
        category.categoryDiagnosisTypes.some(
          (cdt) => cdt.id === d.categoryDiagnosisTypeId,
        ),
      );
      const diagnosisText =
        wheelchairDiagnoses
          .flatMap((d) =>
            category.categoryDiagnosisTypes.flatMap((cdt) =>
              generateCategoryDiagnosisTypeDisplays(
                [d],
                cdt,
                category.defaultIcd10Descriptions || [],
              ).flat(),
            ),
          )
          .filter(Boolean)
          .join(", ") || "\u2014";

      const planOfCare = props.planOfCares.find(
        (poc) => poc.categoryId === category.id,
      );
      const lengthOfNeedDisplay = planOfCare?.lengthOfNeedInMonths
        ? `${planOfCare.lengthOfNeedInMonths} months`
        : "\u2014";
      const heightDisplay =
        planOfCare?.patientHeightFeet != null &&
        planOfCare?.patientHeightInches != null
          ? `${planOfCare.patientHeightFeet}'${planOfCare.patientHeightInches}"`
          : "\u2014";
      const weightDisplay = planOfCare?.patientWeight
        ? `${planOfCare.patientWeight} lbs`
        : "\u2014";

      return (
        <View key={category.id}>
          <Text style={styles.sectionHeader}>
            Order Detail: {category.displayName}
          </Text>
          <View style={styles.borderBottom} />

          <Text style={styles.subheaderText}>Order Info:</Text>
          <View style={styles.flex}>
            {(
              [
                ["Diagnosis", diagnosisText],
                ["Length of Need", lengthOfNeedDisplay],
                ["Patient's Height", heightDisplay],
                ["Patient's Weight", weightDisplay],
              ] as [string, string][]
            ).map(([label, value]) => (
              <View style={styles.row} key={label}>
                <View style={{ ...styles.labelCell, width: 110 }}>
                  <Text>{label}</Text>
                </View>
                <View style={{ ...styles.infoCell, width: 187 }}>
                  <Text>{value}</Text>
                </View>
              </View>
            ))}
          </View>

          <Text style={styles.subheaderText}>Ordered Items:</Text>
          <View style={styles.flex}>
            <View style={styles.row}>
              {(
                [
                  ["Wheelchair Type", 120],
                  ["HCPCS", 72],
                  ["Options", 120],
                  ["Cushions", 120],
                ] as [string, number][]
              ).map(([header, width]) => (
                <View key={header} style={{ ...styles.headerCell, width }}>
                  <Text>{header}</Text>
                </View>
              ))}
            </View>
            {wheelchairTypeItems.length === 0 ? (
              <View style={styles.row}>
                <View style={{ ...styles.infoCell, width: 432 }}>
                  <Text>{"\u2014"}</Text>
                </View>
              </View>
            ) : (
              wheelchairTypeItems.map((typeItem, index) => {
                const swoOverride = getBariatricWheelchairItemOverride(
                  typeItem.formItemName,
                  planOfCare?.patientWeight,
                );
                const displayName = swoOverride?.name ?? typeItem.formItemName;
                const displayHcpcs =
                  swoOverride?.hcpcsCode ?? typeItem.hcpcsCode ?? "\u2014";
                return (
                  <View style={styles.row} key={index}>
                    <View style={{ ...styles.infoCell, width: 120 }}>
                      <Text>{displayName}</Text>
                    </View>
                    <View style={{ ...styles.infoCell, width: 72 }}>
                      <Text>{displayHcpcs}</Text>
                    </View>
                    <View style={{ ...styles.infoCell, width: 120 }}>
                      <Text>
                        {[
                          ...(
                            variantIdToOptionIds.get(
                              typeItem.formItemVariantId,
                            ) ?? []
                          )
                            .map((id) => optionIdToInfo.get(id))
                            .filter(
                              (info) =>
                                info != null &&
                                info.optionName !== "Unselected",
                            )
                            .map(
                              (info) =>
                                `${info!.groupName}: ${info!.optionName}`,
                            ),
                          ...optionsItems.map((li) => li.formItemName),
                        ].join(", ") || "\u2014"}
                      </Text>
                    </View>
                    <View style={{ ...styles.infoCell, width: 120 }}>
                      <Text>
                        {cushionsItems
                          .map((li) => li.formItemName)
                          .join(", ") || "\u2014"}
                      </Text>
                    </View>
                  </View>
                );
              })
            )}
          </View>
        </View>
      );
    }

    // Plan Of Care
    const planOfCareHeadersAndDisplays =
      generatePlanOfCareHeadersAndDisplaysForCategory(
        category,
        props.planOfCares,
      );
    const planOfCareHeaders = planOfCareHeadersAndDisplays.map(
      ({ header }) => header,
    );
    const planOfCareDisplays = planOfCareHeadersAndDisplays.map(
      ({ display }) => display,
    );

    // Line Items
    const lineItems = props.modernLineItems.filter(
      (li) => li.categoryId === categoryId,
    );

    const planOfCare = props.planOfCares.find(
      (poc) => poc.categoryId === category.id,
    );

    const lineItemHeaders = ["Item Name", "Item Options", "Details"];
    const lineItemValues = lineItems
      .filter((lineItem) => lineItem.categoryId === categoryId)
      .map((lineItem) => {
        const strippedOptions =
          category.identifier === "HOSPITAL_BED"
            ? getTrapezeSwoDisplayOptions(
                lineItem.formItemName,
                lineItem.formItemOptions,
              )
            : lineItem.formItemOptions;

        const trapezeWeightCapacityOptionName =
          category.identifier === "HOSPITAL_BED" &&
          strippedOptions.length === 1 &&
          strippedOptions[0] === "Default"
            ? getTrapezeWeightCapacityOptionName(
                lineItem.formItemName,
                planOfCare?.patientWeight,
              )
            : null;

        const displayOptions = trapezeWeightCapacityOptionName
          ? [trapezeWeightCapacityOptionName]
          : strippedOptions;
        const row = [
          lineItem.customProductName ?? lineItem.formItemName,
          displayOptions.join(", "),
          formatModernLineItemValuesForSummary(lineItem),
        ];
        return row;
      });

    return (
      <View key={category.id}>
        <Text style={styles.sectionHeader}>
          {getSwoCategoryHeader(category, lineItems)}
        </Text>

        <Text style={styles.subheaderText}>Plan of Care</Text>
        <View style={styles.flex}>
          <View style={styles.row}>
            {planOfCareHeaders.map((header, index) => {
              return (
                <View style={{ ...styles.headerCell, width: 170 }} key={index}>
                  <Text>{header}</Text>
                </View>
              );
            })}
          </View>
          <View style={styles.row}>
            {planOfCareDisplays.map((value, index) => {
              return (
                <View style={styles.infoCell} key={index}>
                  <Text>{value}</Text>
                </View>
              );
            })}
          </View>
        </View>

        {category.categoryDiagnosisTypes.map((categoryDiagnosisType) => {
          const diagnosisType = categoryDiagnosisType.diagnosisType;
          const categoryDiagnosisTypeDisplays =
            generateCategoryDiagnosisTypeDisplays(
              props.diagnoses,
              categoryDiagnosisType,
              category.defaultIcd10Descriptions || [],
            );

          return (
            <View key={categoryDiagnosisType.id}>
              <Text style={styles.subheaderText}>
                {titleCase(diagnosisType.type)}
              </Text>
              <View style={styles.flex}>
                <View style={styles.row}>
                  {diagnosisType.diagnosisFields.map((field, index: number) => {
                    return (
                      <View
                        style={{ ...styles.headerCell, width: 170 }}
                        key={index}
                      >
                        <Text>{field.display}</Text>
                      </View>
                    );
                  })}
                </View>
                {categoryDiagnosisTypeDisplays.map(
                  (displayRow, rowIdx: number) => {
                    return displayRow.map((display, idx) => (
                      <View
                        style={styles.row}
                        key={`${categoryDiagnosisType.id}-${rowIdx}-${idx}`}
                      >
                        <View style={styles.infoCell} key={idx}>
                          <Text>{display}</Text>
                        </View>
                      </View>
                    ));
                  },
                )}
              </View>
            </View>
          );
        })}

        <Text style={styles.subheaderText}>Items</Text>
        <View style={styles.flex}>
          <View style={styles.row}>
            {lineItemHeaders.map((header, index) => {
              return (
                <View style={{ ...styles.headerCell, width: 170 }} key={index}>
                  <Text>{header}</Text>
                </View>
              );
            })}
          </View>
          {lineItemValues.map((lineItemRow, index) => {
            return (
              <View style={styles.row} key={index}>
                {lineItemRow.map((value, i) => {
                  return (
                    <View style={styles.infoCell} key={i}>
                      <Text>{value}</Text>
                    </View>
                  );
                })}
              </View>
            );
          })}
        </View>
      </View>
    );
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.fullWidth}>
          <View style={{ ...styles.fullWidth, ...styles.flex, ...styles.row }}>
            {props.automaticRoutedOrder ? (
              <View style={{ ...styles.halfWidth, ...{ marginRight: "10px" } }}>
                <Text style={styles.aboutText}>Ordering Facility</Text>
                <View
                  style={{
                    ...styles.halfWidth,
                    ...{
                      padding: "2px",
                      border: "1px solid #C0C0C0",
                      fontSize: "10px",
                    },
                  }}
                >
                  <Text style={{ ...styles.bold, ...{ marginBottom: "10px" } }}>
                    {props.clinicInfo?.hospitalName}
                  </Text>
                </View>
              </View>
            ) : (
              <View style={styles.halfWidth}>
                <Image
                  style={styles.logoImage}
                  source={props.logoSrc || "/alternate_logo.png"}
                />
                <Text style={styles.aboutText}>Tel: (833) 518-1613</Text>
                <Text style={styles.aboutText}>Fax: (833) 672-2753</Text>
                <Text style={styles.aboutText}>
                  Web: https://versemedical.com
                </Text>
                <Text style={styles.aboutText}>
                  Reference #: {props.orderNumber}
                </Text>
              </View>
            )}
            <View style={{ ...styles.halfWidth }}>
              <Text style={styles.headerRight}>
                Physician Standard Written Order
              </Text>
              <Text style={styles.subheaderTextRight}>
                Order Date: {props.createdAt.split("T")[0]}
              </Text>
            </View>
          </View>

          <View>
            <View style={{ ...styles.flex, ...styles.row }}>
              <View style={styles.halfWidth}>
                <Text style={styles.sectionHeader}>Patient Info</Text>
                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>Patient Name</Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>{props.patientName}</Text>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>DOB / Gender</Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>
                        {props.patientDob} / {props.gender}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>
                        {props.secondaryPhoneNumber ? "Mobile Phone" : "Phone"}
                      </Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>{props.phoneNumber}</Text>
                    </View>
                  </View>

                  {!!props.secondaryPhoneNumber && (
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Secondary Phone</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>{props.secondaryPhoneNumber}</Text>
                      </View>
                    </View>
                  )}

                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>Address</Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>
                        {props.shippingAddress.address1}
                        {props.shippingAddress.address2
                          ? ` ${props.shippingAddress.address2}, `
                          : ", "}
                        {props.shippingAddress.city},{" "}
                        {props.shippingAddress.stateName}{" "}
                        {props.shippingAddress.zipcode}
                      </Text>
                    </View>
                  </View>
                  {props.clinicDelivery && (
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Ship To (Clinic)</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.clinicDelivery.address.address1}
                          {props.clinicDelivery.address.address2
                            ? ` ${props.clinicDelivery.address.address2}, `
                            : ", "}
                          {props.clinicDelivery.address.city},{" "}
                          {props.clinicDelivery.address.stateName}{" "}
                          {props.clinicDelivery.address.zipcode}
                        </Text>
                        {!!props.clinicDelivery.phone && (
                          <Text>Phone: {props.clinicDelivery.phone}</Text>
                        )}
                        {!!props.clinicDelivery.deliveryInstructions && (
                          <Text>
                            Instructions:{" "}
                            {props.clinicDelivery.deliveryInstructions}
                          </Text>
                        )}
                      </View>
                    </View>
                  )}
                  {props.automaticRoutedOrder && (
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Primary Insurance</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>{props.insuranceInfo?.primaryInsurance}</Text>
                        <Text>
                          Policy #: {props.insuranceInfo?.primaryPolicyNumber}
                        </Text>
                        <Text>
                          Group #: {props.insuranceInfo?.primaryGroupNumber}
                        </Text>
                      </View>
                    </View>
                  )}
                </View>
              </View>
              <View style={styles.halfWidth}>
                <Text style={styles.sectionHeader}>Prescriber Info</Text>
                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>Signer Name</Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>{props.signerInfo?.signerName}</Text>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>Clinic Name</Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>
                        {props.clinicInfo?.hospitalName} -{" "}
                        {props.clinicInfo?.departmentName}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>Clinic Address</Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>
                        {props.clinicInfo?.address1 != null ? (
                          <>
                            {props.clinicInfo?.address1}
                            {props.clinicInfo?.address2
                              ? " " + props.clinicInfo?.address2
                              : ""}
                            ,{" " + props.clinicInfo?.city},
                            {" " + props.clinicInfo?.stateAbbr}
                            {" " + props.clinicInfo?.zipcode}
                          </>
                        ) : (
                          ""
                        )}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>Clinic Phone</Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>{props.clinicInfo?.phone}</Text>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.labelCell}>
                      <Text>Clinic Fax</Text>
                    </View>
                    <View style={styles.infoCell}>
                      <Text>{props.clinicInfo?.fax}</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>

            {props.woundOrder?.woundCareLineItems != null &&
            props.woundOrder.woundCareLineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Wound Care
                </Text>
                <View style={styles.borderBottom} />

                <Text style={styles.subheaderText}>Wounds:</Text>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View
                      style={{
                        ...styles.headerCell,
                        ...{ width: "60px !important" },
                      }}
                    >
                      <Text>Wound #</Text>
                    </View>
                    <View
                      style={{
                        ...styles.headerCell,
                        ...{ width: "180px !important" },
                      }}
                    >
                      <Text>Description (ICD-10)</Text>
                    </View>
                    <View
                      style={{
                        ...styles.headerCell,
                        ...{ width: "60px !important" },
                      }}
                    >
                      <Text>Thickness</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Location</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Dimensions (cm)</Text>
                    </View>
                    <View
                      style={{
                        ...styles.headerCell,
                        ...{ width: "60px !important" },
                      }}
                    >
                      <Text>Drainage</Text>
                    </View>
                    <View
                      style={{
                        ...styles.headerCell,
                        ...{ width: "60px !important" },
                      }}
                    >
                      <Text>Debrided?</Text>
                    </View>
                  </View>

                  {props.woundOrder.wounds.map((wound, index) => {
                    return (
                      <View style={styles.row} key={index}>
                        <View
                          style={{
                            ...styles.infoCell,
                            ...{ width: "102px !important" },
                          }}
                        >
                          <Text>{index + 1}</Text>
                        </View>
                        <View
                          style={{
                            ...styles.infoCell,
                            ...{ width: "306px !important" },
                          }}
                        >
                          <Text>{wound.description}</Text>
                        </View>
                        <View
                          style={{
                            ...styles.infoCell,
                            ...{ width: "102px !important" },
                          }}
                        >
                          <Text>{wound.thickness}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{wound.location}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {wound.dimensionLength} x {wound.dimensionWidth} x{" "}
                            {wound.dimensionDepth}
                          </Text>
                        </View>
                        <View
                          style={{
                            ...styles.infoCell,
                            ...{ width: "102px !important" },
                          }}
                        >
                          <Text>{mapDrainage(wound.amountExudate || "")}</Text>
                        </View>
                        <View
                          style={{
                            ...styles.infoCell,
                            ...{ width: "102px !important" },
                          }}
                        >
                          <Text>{wound.debrided ?? ""}</Text>
                        </View>
                      </View>
                    );
                  })}
                </View>

                {mergedWoundCareLineItems.length && (
                  <>
                    <Text style={styles.subheaderText}>
                      Ordered Dressings / Supplies:
                    </Text>

                    <View style={styles.flex}>
                      <View style={styles.row}>
                        <View
                          style={{
                            ...styles.headerCell,
                            ...{ width: "180px !important" },
                          }}
                        >
                          <Text>Product Name</Text>
                        </View>
                        <View style={styles.headerCell}>
                          <Text>Size</Text>
                        </View>
                        <View style={styles.headerCell}>
                          <Text>Frequency</Text>
                        </View>
                        <View style={styles.headerCell}>
                          <Text>Duration</Text>
                        </View>
                        <View style={styles.headerCell}>
                          <Text>Wound Selected</Text>
                        </View>
                        <View style={styles.headerCell}>
                          <Text>Total Quantity</Text>
                        </View>
                      </View>

                      {mergedWoundCareLineItems.map((li, index) => {
                        const wli = li;

                        const woundNumbers: string = wli.wounds
                          .map((woundId) => {
                            return (
                              props.woundOrder.wounds
                                .map((wound) => parseInt(wound.id))
                                // @ts-ignore
                                .indexOf(parseInt(woundId) ?? 0) + 1
                            );
                          })
                          .sort()
                          .join(", ");

                        return (
                          <View style={styles.row} key={index}>
                            <View
                              style={{
                                ...styles.infoCell,
                                ...{ width: "306px !important" },
                              }}
                            >
                              <Text>{wli.productName}</Text>
                            </View>
                            <View style={styles.infoCell}>
                              <Text>{wli.size?.name}</Text>
                            </View>
                            <View style={styles.infoCell}>
                              <Text>{wli.frequencyOfUse}</Text>
                            </View>
                            <View style={styles.infoCell}>
                              <Text>{wli.durationOfUse}</Text>
                            </View>
                            <View style={styles.infoCell}>
                              <Text>
                                {woundNumbers !== "0"
                                  ? `Wound(s) ${woundNumbers}`
                                  : ""}
                              </Text>
                            </View>
                            <View style={styles.infoCell}>
                              <Text>
                                {wli.totalQuantity != null
                                  ? String(wli.totalQuantity)
                                  : ""}
                              </Text>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  </>
                )}
              </View>
            ) : null}

            {compressionLineItems.length > 0 && (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Compression items
                </Text>
                <View style={styles.borderBottom} />
                {(props.compressionOrder?.diagnoses || []).length > 0 && (
                  <View style={styles.row}>
                    <View style={{ ...styles.labelCell }}>
                      <Text>Diagnoses:</Text>
                    </View>
                    <View style={{ ...styles.infoCell, width: "300px" }}>
                      {(props.compressionOrder?.diagnoses || []).map(
                        (diagnosis, index) => {
                          return (
                            <View
                              key={`lymphedema-diagnosis-${index}`}
                              style={{ flexDirection: "row" }}
                            >
                              <Text>{index + 1}. </Text>
                              <Text>{diagnosis.diagnosis}</Text>
                            </View>
                          );
                        },
                      )}
                    </View>
                  </View>
                )}
              </View>
            )}

            {wrapStockingCompressionItems.length > 0 && (
              <View style={styles.row}>
                <View style={styles.flex}>
                  <Text style={styles.subheaderText}>
                    Compression Measurements:
                  </Text>

                  <View style={{ ...styles.row, ...styles.halfWidth }}>
                    <View style={styles.headerCell}>
                      <Text>Leg</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Ankle</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Calf</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Thigh</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Leg Length</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Midfoot Circumference</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Foot Length</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Shoe Size</Text>
                    </View>
                  </View>

                  {
                    <>
                      <View style={{ ...styles.row, ...styles.halfWidth }}>
                        <View style={styles.infoCell}>
                          <Text>Left</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.leftAnkle || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.leftCalf || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.thighLeftLength || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.legLeftLength || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.leftFootCircumference || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.leftFootLength || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.leftFootShoeSize || ""}
                          </Text>
                        </View>
                      </View>
                      <View style={{ ...styles.row, ...styles.halfWidth }}>
                        <View style={styles.infoCell}>
                          <Text>Right</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.rightAnkle || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.rightCalf || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.thighRightLength || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.legRightLength || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.rightFootCircumference ||
                              ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.rightFootLength || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.rightFootShoeSize || ""}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.row}>
                        <Text style={styles.subheaderText}>
                          Compression Level:{" "}
                        </Text>
                        <Text
                          style={{
                            ...styles.aboutText,
                            ...styles.marginTop,
                          }}
                        >
                          {compressionPlanOfCare?.compressionLevelWrapStocking ||
                            ""}
                        </Text>
                      </View>
                    </>
                  }
                </View>

                <View style={{ ...styles.flex, ...{ paddingLeft: "50px" } }}>
                  <Text style={styles.subheaderText}>
                    Ordered Compression Items:
                  </Text>

                  {wrapStockingCompressionItems.map((cli, index) => {
                    if (cli.subCategory === ProductSubCategory.Wrap) {
                      return (
                        <View
                          key={`${cli.productName}-${index}`}
                          style={{ ...styles.row, ...{ width: "220px" } }}
                        >
                          <View style={styles.headerCell}>
                            <Text>Compression Wrap</Text>
                          </View>
                          <View style={styles.infoCell}>
                            <Text>{cli.productName}</Text>
                            {cli.side && (
                              <Text>
                                Side: {cli.side === "LEFT" ? "L" : "R"}
                              </Text>
                            )}
                            {cli.quantity && <Text>Qty: {cli.quantity}</Text>}
                            {cli.formItemOptionNames?.length && (
                              <View>
                                {(cli.formItemOptionNames || []).map(
                                  (optionName: string, index) => {
                                    return (
                                      <Text key={`${optionName}-${index}`}>
                                        &#8226; {optionName}
                                      </Text>
                                    );
                                  },
                                )}
                              </View>
                            )}
                          </View>
                        </View>
                      );
                    } else {
                      return (
                        <View
                          key={`${cli.productName}-${index}`}
                          style={{ ...styles.row, ...{ width: "220px" } }}
                        >
                          <View style={styles.headerCell}>
                            <Text>Compression Stocking</Text>
                          </View>
                          <View style={styles.infoCell}>
                            <Text>{cli.productName}</Text>
                            {cli.side && (
                              <Text>
                                Side: {cli.side === "LEFT" ? "L" : "R"}
                              </Text>
                            )}
                            {cli.quantity && <Text>Qty: {cli.quantity}</Text>}
                            {cli.formItemOptionNames?.length && (
                              <View>
                                {(cli.formItemOptionNames || []).map(
                                  (optionName: string, index) => {
                                    return (
                                      <Text key={`${optionName}-${index}`}>
                                        &#8226; {optionName}
                                      </Text>
                                    );
                                  },
                                )}
                              </View>
                            )}
                          </View>
                        </View>
                      );
                    }
                  })}
                </View>
              </View>
            )}

            {(compressionLineItemMap.get(ProductSubCategory.ArmSleeve) ?? [])
              .length > 0 && (
              <View style={styles.row}>
                <View style={styles.flex}>
                  <Text style={styles.subheaderText}>
                    Compression Measurements:
                  </Text>

                  <View style={{ ...styles.row, ...styles.halfWidth }}>
                    <View style={styles.headerCell}>
                      <Text>Arm</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Hand (A)</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Wrist 1 (C)</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Wrist 2 (C1)</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Elbow (E)</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Upper Arm (G)</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Length</Text>
                    </View>
                  </View>

                  {
                    <>
                      <View style={{ ...styles.row, ...styles.halfWidth }}>
                        <View style={styles.infoCell}>
                          <Text>Left</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.leftHand || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.leftWrist1 || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.leftWrist2 || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.leftElbow || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.leftUpperArm || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.armLeftLength || ""}
                          </Text>
                        </View>
                      </View>
                      <View style={{ ...styles.row, ...styles.halfWidth }}>
                        <View style={styles.infoCell}>
                          <Text>Right</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.rightHand || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.rightWrist1 || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.rightWrist2 || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{compressionPlanOfCare?.rightElbow || ""}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.rightUpperArm || ""}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.armRightLength || ""}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.row}>
                        <Text style={styles.subheaderText}>
                          Compression Level:{" "}
                        </Text>
                        <Text
                          style={{
                            ...styles.aboutText,
                            ...styles.marginTop,
                          }}
                        >
                          {compressionPlanOfCare?.compressionLevelSleeve || ""}
                        </Text>
                      </View>
                    </>
                  }
                </View>

                <View style={{ ...styles.flex, ...{ paddingLeft: "50px" } }}>
                  <Text style={styles.subheaderText}>
                    Ordered Compression Items:
                  </Text>

                  {(
                    compressionLineItemMap.get(ProductSubCategory.ArmSleeve) ??
                    []
                  ).map((cli, index) => {
                    return (
                      <View
                        key={`${cli.productName}-${index}`}
                        style={{ ...styles.row, ...{ width: "220px" } }}
                      >
                        <View style={styles.headerCell}>
                          <Text>Compression Arm Sleeves</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{cli.productName}</Text>
                          {cli.side && (
                            <Text>Side: {cli.side === "LEFT" ? "L" : "R"}</Text>
                          )}
                          {cli.quantity && <Text>Qty: {cli.quantity}</Text>}
                          {cli.formItemOptionNames?.length && (
                            <View>
                              {(cli.formItemOptionNames || []).map(
                                (optionName: string, index) => {
                                  return (
                                    <Text key={`${optionName}-${index}`}>
                                      &#8226; {optionName}
                                    </Text>
                                  );
                                },
                              )}
                            </View>
                          )}
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            )}

            {(
              compressionLineItemMap.get(ProductSubCategory.CompressionBra) ??
              []
            ).length > 0 && (
              <View style={styles.row}>
                <View style={styles.flex}>
                  <Text style={styles.subheaderText}>
                    Compression Measurements:
                  </Text>

                  <View style={{ ...styles.row, ...styles.halfWidth }}>
                    <View style={styles.headerCell}>
                      <Text>Band</Text>
                    </View>
                  </View>

                  {
                    <>
                      <View style={{ ...styles.row, ...styles.halfWidth }}>
                        <View style={styles.infoCell}>
                          <Text>
                            {compressionPlanOfCare?.compressionBraBandSize ||
                              ""}
                          </Text>
                        </View>
                      </View>
                    </>
                  }
                </View>

                <View style={{ ...styles.flex, ...{ paddingLeft: "50px" } }}>
                  <Text style={styles.subheaderText}>
                    Ordered Compression Items:
                  </Text>

                  {(
                    compressionLineItemMap.get(
                      ProductSubCategory.CompressionBra,
                    ) ?? []
                  ).map((cli, index) => {
                    return (
                      <View
                        key={`${cli.productName}-${index}`}
                        style={{ ...styles.row, ...{ width: "220px" } }}
                      >
                        <View style={styles.headerCell}>
                          <Text>Compression Bra</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{cli.productName}</Text>
                          {cli.formItemOptionNames?.length && (
                            <View>
                              {(cli.formItemOptionNames || []).map(
                                (optionName: string, index) => {
                                  return (
                                    <Text key={`${optionName}-${index}`}>
                                      &#8226; {optionName}
                                    </Text>
                                  );
                                },
                              )}
                            </View>
                          )}
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            )}

            {props.urologyOrder?.urologyLineItems != null &&
            props.urologyOrder.urologyLineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>Order Detail: Urology</Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Start Date</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>{props.urologyOrder.startDate}</Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.urologyOrder.lengthOfNeedLifetime
                            ? "Lifetime"
                            : props.urologyOrder.lengthOfNeedOtherMonths +
                              " months"}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>UTI History</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.urologyOrder?.utiHistory ? "Yes" : "No"}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Latex Allergy</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.urologyOrder.latexAllergy ? "Yes" : "No"}
                        </Text>
                      </View>
                    </View>
                  </View>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Diagnoses:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>ICD10 Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        {props.urologyOrder.ensuresisF980 && (
                          <Text>
                            Enuresis not due to a substance or known
                            physiological condition F98.0
                          </Text>
                        )}
                        {props.urologyOrder.neuromuscularN319 && (
                          <Text>
                            Neuromuscular dysfunction of bladder, unspecified
                            N31.9
                          </Text>
                        )}
                        {props.urologyOrder.retentionR339 && (
                          <Text>Retention of urine, unspecified R33.9</Text>
                        )}
                        {props.urologyOrder.nephrostomyZ436 && (
                          <Text>
                            Encounter for attention to other artificial openings
                            of urinary tract Z43.6
                          </Text>
                        )}
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Primary / Causal Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>{props.urologyOrder.primaryCasualDiagnosis}</Text>
                      </View>
                    </View>
                  </View>
                </View>

                <Text style={styles.subheaderText}>Ordered Items:</Text>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View
                      style={{
                        ...styles.headerCell,
                        ...{ width: "180px !important" },
                      }}
                    >
                      <Text>Product Name</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Brand Preference</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>French Size/Length</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Frequency</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Quantity / Month</Text>
                    </View>
                  </View>

                  {props.urologyOrder.urologyLineItems.map((li, index) => {
                    return (
                      <View
                        style={styles.row}
                        key={`${li.productName}-${index}`}
                      >
                        <View
                          style={{
                            ...styles.infoCell,
                            ...{ width: "306px !important" },
                          }}
                        >
                          <Text style={styles.bold}>{li.productName}</Text>
                          <Text>{li.checkboxAttributes.coude && "Coudè"}</Text>
                          <Text>
                            {li.checkboxAttributes.hydrophilic && "Hydrophilic"}
                          </Text>
                          <Text>{li.checkboxAttributes.latex && "Latex"}</Text>
                          <Text>
                            {li.checkboxAttributes.packets && "Packets"}
                          </Text>
                          <Text>{li.checkboxAttributes.pvc && "PVC"}</Text>
                          <Text>
                            {li.checkboxAttributes.redRubber && "Red Rubber"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.silicone && "Silicone"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.straight && "Straight"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.threeWay && "Three Way"}
                          </Text>
                          <Text>{li.checkboxAttributes.tube && "Tube"}</Text>
                          <Text>
                            {li.checkboxAttributes.twoWay && "Two Way"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.withBag && "With Bag"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.withoutBag && "Without Bag"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.regular2000ml &&
                              "Regular 2000ml"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.large4000ml &&
                              "Large 4000ml"}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{li.brandPreference}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{li.frenchSizeLength}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{li.frequencyOfUse}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{li.qtyMo}</Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            ) : null}

            {props.ostomyOrder?.ostomyLineItems != null &&
            props.ostomyOrder.ostomyLineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>Order Detail: Ostomy</Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Start Date</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>{props.ostomyOrder.startDate}</Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.ostomyOrder.lengthOfNeedLifetime
                            ? "Lifetime"
                            : props.ostomyOrder.lengthOfNeedOtherMonths +
                              " months"}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Latex Allergy</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.ostomyOrder.latexAllergy ? "Yes" : "No"}
                        </Text>
                      </View>
                    </View>
                  </View>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Diagnoses:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Primary Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.ostomyOrder.z933Colostomy && "Z93.3 Colostomy"}
                        </Text>
                        <Text>
                          {props.ostomyOrder.z936Urostomy && "Z93.6 Urostomy"}
                        </Text>
                        <Text>
                          {props.ostomyOrder.z932Ileostomy && "Z93.2 Ileostomy"}
                        </Text>
                        <Text>{props.ostomyOrder.primaryDiagnosisOther}</Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Secondary Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.ostomyOrder.colonCancer && "Colon Cancer"}
                        </Text>
                        <Text>
                          {props.ostomyOrder.ulcerativeColitis &&
                            "Ulcerative Colitis"}
                        </Text>
                        <Text>
                          {props.ostomyOrder.perforatedBowel &&
                            "Perforated Bowel"}
                        </Text>
                        <Text>
                          {props.ostomyOrder.bladderCancer && "Bladder Cancer"}
                        </Text>
                        <Text>
                          {props.ostomyOrder.crohnsDisease && "Crohn's Disease"}
                        </Text>
                        <Text>
                          {props.ostomyOrder.bowelObstruction &&
                            "Bowel Obstruction"}
                        </Text>
                        <Text>{props.ostomyOrder.secondaryDiagnosisOther}</Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Additional Justification</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>{props.ostomyOrder.additionalJustification}</Text>
                      </View>
                    </View>
                  </View>
                </View>

                <Text style={styles.subheaderText}>Ordered Items:</Text>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View
                      style={{
                        ...styles.headerCell,
                        ...{ width: "180px !important" },
                      }}
                    >
                      <Text>Product Name</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Brand Preference</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Product #</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Frequency of Use</Text>
                    </View>
                    <View style={styles.headerCell}>
                      <Text>Quantity / Month</Text>
                    </View>
                  </View>

                  {props.ostomyOrder.ostomyLineItems.map((li, index) => {
                    return (
                      <View
                        key={`${li.productName}-${index}`}
                        style={styles.row}
                      >
                        <View
                          style={{
                            ...styles.infoCell,
                            ...{ width: "306px !important" },
                          }}
                        >
                          <Text style={styles.bold}>{li.productName}</Text>
                          <Text>
                            {li.checkboxAttributes.bagTwoThousandCc &&
                              "Bag 2000cc"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.bottle && "Bottle"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.closed && "Closed"}
                          </Text>
                          <Text>{li.checkboxAttributes.cloth && "Cloth"}</Text>
                          <Text>{li.checkboxAttributes.drain && "Drain"}</Text>
                          <Text>
                            {li.checkboxAttributes.fourInch && "4 inch"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.karayaFourHalfOz &&
                              "Karaya 4.5oz"}
                          </Text>
                          <Text>{li.checkboxAttributes.large && "Large"}</Text>
                          <Text>
                            {li.checkboxAttributes.medium && "Medium"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.oneInch && "1 inch"}
                          </Text>
                          <Text>{li.checkboxAttributes.paper && "Paper"}</Text>
                          <Text>
                            {li.checkboxAttributes.pectinTwoOz && "Pectin 2oz"}
                          </Text>
                          <Text>{li.checkboxAttributes.pink && "Pink"}</Text>
                          <Text>
                            {li.checkboxAttributes.twoInch && "2 inch"}
                          </Text>
                          <Text>
                            {li.checkboxAttributes.urostomy && "Urostomy"}
                          </Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{li.brandPreference}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{li.productNum}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{li.dailyFrequencyOfUse}</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>{li.qtyMo}</Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            ) : null}
            {/* CPAP Order Section */}
            {props.cpapOrder?.cpapLineItems != null &&
            props.cpapOrder.cpapLineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>Order Detail: CPAP</Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        {props.cpapOrder.cpapDiagnoses.map((diag, idx) => (
                          <Text key={`cpap-diag-${idx}`}>
                            {idx + 1}.{" "}
                            {diag.icd10Code
                              ? `${diag.icd10Code}: ${diag.diagnosis ?? ""}`
                              : (diag.diagnosis ?? "")}
                          </Text>
                        ))}
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.cpapOrder.lengthOfNeedType === "lifetime"
                            ? "Lifetime"
                            : props.cpapOrder.lengthOfNeedMonths
                              ? `${props.cpapOrder.lengthOfNeedMonths} months`
                              : ""}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>

                <Text style={styles.subheaderText}>Ordered Items:</Text>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "180px" }}>
                      <Text>Product Name</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "80px" }}>
                      <Text>HCPCs</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "280px" }}>
                      <Text>Prescription / Specification</Text>
                    </View>
                  </View>

                  {props.cpapOrder.cpapLineItems.map((li, index) => {
                    const specs = getCpapLineItemSpecs(
                      li.productName,
                      props.cpapOrder!,
                    );
                    return (
                      <View
                        style={styles.row}
                        key={`cpap-li-${li.id}-${index}`}
                      >
                        <View style={{ ...styles.infoCell, width: "180px" }}>
                          <Text style={styles.bold}>{li.productName}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "80px" }}>
                          <Text>{li.hcpcsCode ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "280px" }}>
                          {specs.map((spec, specIdx) => (
                            <Text key={`cpap-spec-${index}-${specIdx}`}>
                              {spec}
                            </Text>
                          ))}
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            ) : null}
            {/* Shell form sections -- one per form the provider filled in.
                Each has its own heading from shellFormConfig, so adding a form
                needs no change here. */}
            {(props.shellFormOrders ?? [])
              .filter((o) => (o.lineItems ?? []).length > 0)
              .map((shellFormOrder) => (
                <View key={shellFormOrder.productCategory}>
                  <Text style={styles.sectionHeader}>
                    {`Order Detail: ${SHELL_FORM_CONFIG[shellFormOrder.productCategory]?.displayName ?? shellFormOrder.productCategory}`}
                  </Text>
                  <View style={styles.borderBottom} />

                  <View style={styles.row}>
                    <View style={styles.flex}>
                      <Text style={styles.subheaderText}>Order Info:</Text>
                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Diagnosis</Text>
                        </View>
                        <View style={styles.infoCell}>
                          {shellFormOrder.diagnoses.map((diag, idx) => (
                            <Text key={`pneu-diag-${idx}`}>
                              {idx + 1}.{" "}
                              {diag.icd10Code
                                ? `${diag.icd10Code}: ${diag.diagnosis ?? ""}`
                                : (diag.diagnosis ?? "")}
                            </Text>
                          ))}
                        </View>
                      </View>

                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Length of Need</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {shellFormOrder.isLifetimeLengthOfNeed
                              ? "Lifetime or maximum allowed time"
                              : shellFormOrder.lengthOfNeedInMonths
                                ? `${shellFormOrder.lengthOfNeedInMonths} months`
                                : ""}
                          </Text>
                        </View>
                      </View>

                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Preferred DME</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {shellFormOrder.preferredDmeCompany ?? ""}
                          </Text>
                        </View>
                      </View>

                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Medical necessity attested</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {shellFormOrder.meetsMedicalNecessity
                              ? "Yes"
                              : "No"}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  <Text style={styles.subheaderText}>Ordered Items:</Text>

                  <View style={styles.flex}>
                    <View style={styles.row}>
                      <View style={{ ...styles.headerCell, width: "180px" }}>
                        <Text>Product Name</Text>
                      </View>
                      <View style={{ ...styles.headerCell, width: "200px" }}>
                        <Text>Size or Specifications</Text>
                      </View>
                      <View style={{ ...styles.headerCell, width: "60px" }}>
                        <Text>Qty</Text>
                      </View>
                      <View style={{ ...styles.headerCell, width: "160px" }}>
                        <Text>Remarks</Text>
                      </View>
                    </View>

                    {shellFormOrder.lineItems.map((li, index) => (
                      <View
                        style={styles.row}
                        key={`shell-li-${li.id}-${index}`}
                      >
                        <View style={{ ...styles.infoCell, width: "180px" }}>
                          <Text style={styles.bold}>{li.productName}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "200px" }}>
                          <Text>{li.sizeOrSpecifications ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "60px" }}>
                          <Text>
                            {li.quantity != null ? String(li.quantity) : ""}
                          </Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "160px" }}>
                          <Text>{li.remarks ?? ""}</Text>
                        </View>
                      </View>
                    ))}
                  </View>
                </View>
              ))}
            {/* Off-the-Shelf Orthotics Order Section */}
            {props.offTheShelfOrthoticsOrder?.offTheShelfOrthoticsLineItems
              ?.length ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Off-the-Shelf Orthotics
                </Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        {props.offTheShelfOrthoticsOrder.offTheShelfOrthoticsDiagnoses.map(
                          (diag, idx) => (
                            <Text key={`orthotics-diag-${idx}`}>
                              {idx + 1}.{" "}
                              {[diag.icd10Code, diag.diagnosis]
                                .filter(Boolean)
                                .join(": ")}
                            </Text>
                          ),
                        )}
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.offTheShelfOrthoticsOrder
                            .isLifetimeLengthOfNeed
                            ? "Lifetime or maximum allowed time"
                            : props.offTheShelfOrthoticsOrder
                                  .lengthOfNeedInMonths
                              ? `${props.offTheShelfOrthoticsOrder.lengthOfNeedInMonths} months`
                              : ""}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>

                <Text style={styles.subheaderText}>Ordered Items:</Text>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "240px" }}>
                      <Text>Item</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "90px" }}>
                      <Text>HCPCS</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "120px" }}>
                      <Text>Size</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "90px" }}>
                      <Text>Side</Text>
                    </View>
                  </View>

                  {props.offTheShelfOrthoticsOrder.offTheShelfOrthoticsLineItems.map(
                    (li, index) => (
                      <View
                        style={styles.row}
                        key={`orthotics-li-${li.id}-${index}`}
                      >
                        <View style={{ ...styles.infoCell, width: "240px" }}>
                          <Text style={styles.bold}>{li.productName}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "90px" }}>
                          <Text>{li.hcpcsCode ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "120px" }}>
                          <Text>{li.sizeWithMeasurements ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "90px" }}>
                          <Text>{li.laterality ?? ""}</Text>
                        </View>
                      </View>
                    ),
                  )}
                </View>
              </View>
            ) : null}
            {/* Custom Orthotics Order Section */}
            {props.customOrthoticsOrder?.customOrthoticsLineItems?.length ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Custom Orthotics
                </Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        {props.customOrthoticsOrder.customOrthoticsDiagnoses.map(
                          (diag, idx) => (
                            <Text key={`custom-orthotics-diag-${idx}`}>
                              {idx + 1}.{" "}
                              {[diag.icd10Code, diag.diagnosis]
                                .filter(Boolean)
                                .join(": ")}
                            </Text>
                          ),
                        )}
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.customOrthoticsOrder.isLifetimeLengthOfNeed
                            ? "Lifetime or maximum allowed time"
                            : props.customOrthoticsOrder.lengthOfNeedInMonths
                              ? `${props.customOrthoticsOrder.lengthOfNeedInMonths} months`
                              : ""}
                        </Text>
                      </View>
                    </View>

                    {/* A custom orthosis is fabricated to the patient rather
                        than picked off a size chart, so these measurements are
                        part of the order the orthotist fills. */}
                    {optionalInfoRows([
                      [
                        "Weight",
                        props.customOrthoticsOrder.patientWeightLbs != null
                          ? `${props.customOrthoticsOrder.patientWeightLbs} lbs`
                          : null,
                      ],
                      [
                        "Age",
                        props.customOrthoticsOrder.patientAgeYears != null
                          ? `${props.customOrthoticsOrder.patientAgeYears} years`
                          : null,
                      ],
                      ["Shoe Size", props.customOrthoticsOrder.shoeSize],
                      ["Shoe Width", props.customOrthoticsOrder.shoeWidth],
                      [
                        "Preferred DME",
                        props.customOrthoticsOrder.preferredDmeCompany,
                      ],
                    ])}
                  </View>
                </View>

                <Text style={styles.subheaderText}>Ordered Items:</Text>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "200px" }}>
                      <Text>Item</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "90px" }}>
                      <Text>HCPCS</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "60px" }}>
                      <Text>Side</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "110px" }}>
                      <Text>Qty</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "80px" }}>
                      <Text>Joint Type</Text>
                    </View>
                  </View>

                  {props.customOrthoticsOrder.customOrthoticsLineItems.map(
                    (li, index) => (
                      <View
                        style={styles.row}
                        key={`custom-orthotics-li-${li.id}-${index}`}
                      >
                        <View style={{ ...styles.infoCell, width: "200px" }}>
                          {/* A limb row names no product: the orthosis is not
                              chosen until an orthotist evaluates the patient, so
                              the label is derived from the body parts it covers. */}
                          <Text style={styles.bold}>
                            {customOrthoticsLineItemName(li)}
                          </Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "90px" }}>
                          {/* A list when the code depends on what the
                              orthotist builds; blank for a limb or typed row. */}
                          <Text>{li.hcpcsCode ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "60px" }}>
                          <Text>
                            {customOrthoticsLineItemLaterality(li) ?? ""}
                          </Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "110px" }}>
                          <Text>
                            {customOrthoticsLineItemQuantity(li) ?? ""}
                          </Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "80px" }}>
                          <Text>
                            {customOrthoticsLineItemJointType(li) ?? ""}
                          </Text>
                        </View>
                      </View>
                    ),
                  )}
                </View>

                {customOrthoticsSpecificationRows.length ? (
                  <View>
                    <Text style={styles.subheaderText}>Specifications:</Text>
                    <View style={styles.flex}>
                      {customOrthoticsSpecificationRows}
                    </View>
                  </View>
                ) : null}
              </View>
            ) : null}
            {/* Wheelchairs & Scooters Order Section */}
            {props.wheelchairsAndScootersOrder
              ?.wheelchairsAndScootersLineItems != null &&
            props.wheelchairsAndScootersOrder.wheelchairsAndScootersLineItems
              .length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Wheelchairs & Scooters
                </Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        {props.wheelchairsAndScootersOrder.wheelchairsAndScootersDiagnoses.map(
                          (diag, idx) => (
                            <Text key={`wheelchairs-and-scooters-diag-${idx}`}>
                              {idx + 1}.{" "}
                              {[diag.icd10Code, diag.diagnosis]
                                .filter(Boolean)
                                .join(": ")}
                            </Text>
                          ),
                        )}
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Device</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {[
                            props.wheelchairsAndScootersOrder.deviceType
                              ? (WHEELCHAIRS_AND_SCOOTERS_DEVICE_LABELS[
                                  props.wheelchairsAndScootersOrder.deviceType
                                ] ??
                                props.wheelchairsAndScootersOrder.deviceType)
                              : null,
                            props.wheelchairsAndScootersOrder
                              .otherDeviceDescription,
                          ]
                            .filter(Boolean)
                            .join(": ")}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.wheelchairsAndScootersOrder
                            .isLifetimeLengthOfNeed
                            ? "Lifetime"
                            : props.wheelchairsAndScootersOrder
                                  .lengthOfNeedInMonths
                              ? `${props.wheelchairsAndScootersOrder.lengthOfNeedInMonths} months`
                              : ""}
                        </Text>
                      </View>
                    </View>

                    {optionalInfoRows([
                      [
                        "Height",
                        props.wheelchairsAndScootersOrder.patientHeightFeet !=
                          null ||
                        props.wheelchairsAndScootersOrder.patientHeightInches !=
                          null
                          ? `${props.wheelchairsAndScootersOrder.patientHeightFeet ?? 0} ft ${
                              props.wheelchairsAndScootersOrder
                                .patientHeightInches ?? 0
                            } in`
                          : null,
                      ],
                      [
                        "Weight",
                        props.wheelchairsAndScootersOrder.patientWeightLbs !=
                        null
                          ? `${props.wheelchairsAndScootersOrder.patientWeightLbs} lbs`
                          : null,
                      ],
                      [
                        "Preferred DME",
                        props.wheelchairsAndScootersOrder.preferredDmeCompany,
                      ],
                    ])}
                  </View>
                </View>

                <Text style={styles.subheaderText}>Ordered Items:</Text>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "240px" }}>
                      <Text>Item</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "90px" }}>
                      <Text>HCPCS</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "130px" }}>
                      <Text>Size</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "80px" }}>
                      <Text>Qty</Text>
                    </View>
                  </View>

                  {props.wheelchairsAndScootersOrder.wheelchairsAndScootersLineItems.map(
                    (li, index) => (
                      <View
                        style={styles.row}
                        key={`wheelchairs-and-scooters-li-${li.id}-${index}`}
                      >
                        <View style={{ ...styles.infoCell, width: "240px" }}>
                          <Text style={styles.bold}>{li.productName}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "90px" }}>
                          <Text>{li.hcpcsCode ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "130px" }}>
                          <Text>{li.size ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "80px" }}>
                          <Text>{li.quantity}</Text>
                        </View>
                      </View>
                    ),
                  )}
                </View>
              </View>
            ) : null}
            {/* Nebulizer Order Section */}
            {props.nebulizerOrder?.nebulizerLineItems != null &&
            props.nebulizerOrder.nebulizerLineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Nebulizer
                </Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        {props.nebulizerOrder.nebulizerDiagnoses.map(
                          (diag, idx) => (
                            <Text key={`neb-diag-${idx}`}>
                              {idx + 1}.{" "}
                              {diag.icd10Code
                                ? `${diag.icd10Code}: ${diag.diagnosis ?? ""}`
                                : (diag.diagnosis ?? "")}
                            </Text>
                          ),
                        )}
                      </View>
                    </View>

                    {/* The drugs are administered with the device but not
                        supplied by this order, so they are labelled as such
                        rather than listed among the products below. */}
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Medications (not supplied)</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {nebulizerMedicationNames(props.nebulizerOrder).join(
                            ", ",
                          )}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Dosing</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.nebulizerOrder.dosingInstructions ?? ""}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.nebulizerOrder.isLifetimeLengthOfNeed
                            ? "Lifetime"
                            : props.nebulizerOrder.lengthOfNeedInMonths
                              ? `${props.nebulizerOrder.lengthOfNeedInMonths} months`
                              : ""}
                        </Text>
                      </View>
                    </View>

                    {nebulizerMaskMeasurements(props.nebulizerOrder) ? (
                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Mask Measurements</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {nebulizerMaskMeasurements(props.nebulizerOrder)}
                          </Text>
                        </View>
                      </View>
                    ) : null}
                  </View>
                </View>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "220px" }}>
                      <Text>Product</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "100px" }}>
                      <Text>HCPCS</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "160px" }}>
                      <Text>Size</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "60px" }}>
                      <Text>Qty</Text>
                    </View>
                  </View>

                  {props.nebulizerOrder.nebulizerLineItems.map((li, index) => (
                    <View style={styles.row} key={`neb-li-${li.id}-${index}`}>
                      <View style={{ ...styles.infoCell, width: "220px" }}>
                        <Text style={styles.bold}>{li.productName}</Text>
                      </View>
                      <View style={{ ...styles.infoCell, width: "100px" }}>
                        <Text>{li.hcpcsCode ?? ""}</Text>
                      </View>
                      <View style={{ ...styles.infoCell, width: "160px" }}>
                        <Text>{li.size ?? ""}</Text>
                      </View>
                      <View style={{ ...styles.infoCell, width: "60px" }}>
                        <Text>{String(li.quantity)}</Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
            {/* Ventilator Order Section */}
            {/* The device and the interface are the two line items, so they are
                not also printed above the table. */}
            {props.ventilatorOrder?.ventilatorLineItems != null &&
            props.ventilatorOrder.ventilatorLineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Ventilator
                </Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        {props.ventilatorOrder.ventilatorDiagnoses.map(
                          (diag, idx) => (
                            <Text key={`vent-diag-${idx}`}>
                              {idx + 1}.{" "}
                              {diag.icd10Code
                                ? `${diag.icd10Code}: ${diag.diagnosis ?? ""}`
                                : (diag.diagnosis ?? "")}
                            </Text>
                          ),
                        )}
                      </View>
                    </View>

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.ventilatorOrder.isLifetimeLengthOfNeed
                            ? "Lifetime"
                            : props.ventilatorOrder.lengthOfNeedInMonths
                              ? `${props.ventilatorOrder.lengthOfNeedInMonths} months`
                              : ""}
                        </Text>
                      </View>
                    </View>

                    {props.ventilatorOrder.preferredDmeCompany ? (
                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Preferred DME Company</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {props.ventilatorOrder.preferredDmeCompany}
                          </Text>
                        </View>
                      </View>
                    ) : null}
                  </View>
                </View>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "220px" }}>
                      <Text>Product</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "100px" }}>
                      <Text>HCPCS</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "160px" }}>
                      <Text>Size</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "60px" }}>
                      <Text>Qty</Text>
                    </View>
                  </View>

                  {props.ventilatorOrder.ventilatorLineItems.map(
                    (li, index) => (
                      <View
                        style={styles.row}
                        key={`vent-li-${li.id}-${index}`}
                      >
                        <View style={{ ...styles.infoCell, width: "220px" }}>
                          <Text style={styles.bold}>{li.productName}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "100px" }}>
                          <Text>{li.hcpcsCode ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "160px" }}>
                          <Text>{li.size ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "60px" }}>
                          <Text>{String(li.quantity)}</Text>
                        </View>
                      </View>
                    ),
                  )}
                </View>
              </View>
            ) : null}
            {/* Wound Suction Order Section */}
            {props.woundSuctionOrder?.woundSuctionLineItems != null &&
            props.woundSuctionOrder.woundSuctionLineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Wound Suction
                </Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>

                    {/* The one interview answer this document prints. It is
                        here because a written order with no length of need has
                        to say something about how long therapy runs, and
                        whether the wound is inpatient is what bounds it.

                        Tri-state: an unanswered question prints no row rather
                        than defaulting to "No", which would be an answer the
                        provider never gave. */}
                    {props.woundSuctionOrder.isTherapyInitiatedInpatient !=
                    null ? (
                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Inpatient Setting</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {props.woundSuctionOrder.isTherapyInitiatedInpatient
                              ? "Yes"
                              : "No"}
                          </Text>
                        </View>
                      </View>
                    ) : null}

                    {props.woundSuctionOrder.preferredDmeCompany ? (
                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Preferred DME</Text>
                        </View>
                        <View style={styles.infoCell}>
                          <Text>
                            {props.woundSuctionOrder.preferredDmeCompany}
                          </Text>
                        </View>
                      </View>
                    ) : null}
                  </View>
                </View>

                {woundSuctionWounds.length > 0 ? (
                  <>
                    <Text style={styles.subheaderText}>Wounds Treated:</Text>
                    <View style={styles.flex}>
                      <View style={styles.row}>
                        <View style={{ ...styles.headerCell, width: "60px" }}>
                          <Text>Wound #</Text>
                        </View>
                        <View style={{ ...styles.headerCell, width: "180px" }}>
                          <Text>Description (ICD-10)</Text>
                        </View>
                        <View style={styles.headerCell}>
                          <Text>Location</Text>
                        </View>
                        <View style={styles.headerCell}>
                          <Text>Dimensions (cm)</Text>
                        </View>
                        <View style={{ ...styles.headerCell, width: "80px" }}>
                          <Text>Drainage</Text>
                        </View>
                        {/* The same `debrided` column the Wound Care table
                            above prints as "Debrided?". Labelled by what it
                            holds here, because the Wound Suction form stores
                            three states (debrided, undebrided, surgical) and
                            they are what routes coverage -- a yes/no header
                            over "Surgical" would misread. */}
                        <View style={{ ...styles.headerCell, width: "80px" }}>
                          <Text>Wound Status</Text>
                        </View>
                      </View>

                      {woundSuctionWounds.map((wound, index) => (
                        <View style={styles.row} key={`ws-wound-${index}`}>
                          <View style={{ ...styles.infoCell, width: "60px" }}>
                            <Text>{index + 1}</Text>
                          </View>
                          <View style={{ ...styles.infoCell, width: "180px" }}>
                            <Text>{wound.description ?? ""}</Text>
                          </View>
                          <View style={styles.infoCell}>
                            <Text>{wound.location ?? ""}</Text>
                          </View>
                          <View style={styles.infoCell}>
                            <Text>
                              {wound.dimensionLength} x {wound.dimensionWidth} x{" "}
                              {wound.dimensionDepth}
                            </Text>
                          </View>
                          <View style={{ ...styles.infoCell, width: "80px" }}>
                            <Text>
                              {mapDrainage(wound.amountExudate || "") ?? ""}
                            </Text>
                          </View>
                          <View style={{ ...styles.infoCell, width: "80px" }}>
                            <Text>{titleCase(wound.debrided ?? "")}</Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  </>
                ) : null}

                {woundSuctionTreatments.length > 0 ? (
                  <>
                    <Text style={styles.subheaderText}>Treatment Plan:</Text>
                    <View style={styles.flex}>
                      <View style={styles.row}>
                        <View style={{ ...styles.headerCell, width: "180px" }}>
                          <Text>Wounds</Text>
                        </View>
                        <View style={{ ...styles.headerCell, width: "180px" }}>
                          <Text>Duration of Use</Text>
                        </View>
                        <View style={styles.headerCell}>
                          <Text>Frequency of Change</Text>
                        </View>
                      </View>

                      {woundSuctionTreatments.map((treatment) => (
                        <View style={styles.row} key={`ws-tx-${treatment.id}`}>
                          <View style={{ ...styles.infoCell, width: "180px" }}>
                            <Text>
                              {(treatment.selectedWoundIndices ?? [])
                                .map((woundIndex) => woundIndex + 1)
                                .join(", ")}
                            </Text>
                          </View>
                          <View style={{ ...styles.infoCell, width: "180px" }}>
                            <Text>{treatment.durationOfUse ?? ""}</Text>
                          </View>
                          <View style={styles.infoCell}>
                            <Text>{treatment.frequencyOfChange ?? ""}</Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  </>
                ) : null}

                <Text style={styles.subheaderText}>Ordered Items:</Text>
                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "300px" }}>
                      <Text>Product</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "100px" }}>
                      <Text>HCPCS</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "60px" }}>
                      <Text>Qty</Text>
                    </View>
                  </View>

                  {props.woundSuctionOrder.woundSuctionLineItems.map(
                    (li, index) => (
                      <View style={styles.row} key={`ws-li-${li.id}-${index}`}>
                        <View style={{ ...styles.infoCell, width: "300px" }}>
                          <Text style={styles.bold}>{li.productName}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "100px" }}>
                          <Text>{li.hcpcsCode ?? ""}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "60px" }}>
                          <Text>{String(li.quantity)}</Text>
                        </View>
                      </View>
                    ),
                  )}
                </View>
              </View>
            ) : null}
            {/* Diabetes Order Section */}
            {props.diabetesOrder?.diabetesLineItems != null &&
            props.diabetesOrder.diabetesLineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>Order Detail: Diabetes</Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Diagnosis</Text>
                      </View>
                      <View style={styles.infoCell}>
                        {props.diabetesOrder.diabetesDiagnoses.map(
                          (diag, idx) => (
                            <Text key={`diabetes-diag-${idx}`}>
                              {idx + 1}.{" "}
                              {[diag.icd10Code, diag.diagnosis]
                                .filter(Boolean)
                                .join(": ")}
                            </Text>
                          ),
                        )}
                      </View>
                    </View>

                    {diabetesSystemRows}

                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Length of Need</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.diabetesOrder.isLifetimeLengthOfNeed
                            ? "Lifetime"
                            : props.diabetesOrder.lengthOfNeedInMonths
                              ? `${props.diabetesOrder.lengthOfNeedInMonths} months`
                              : ""}
                        </Text>
                      </View>
                    </View>

                    {optionalInfoRows([
                      [
                        "Preferred DME",
                        props.diabetesOrder.preferredDmeCompany,
                      ],
                    ])}
                  </View>
                </View>

                <Text style={styles.subheaderText}>Ordered Items:</Text>

                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "240px" }}>
                      <Text>Item</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "90px" }}>
                      <Text>HCPCS</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "130px" }}>
                      <Text>Size</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "80px" }}>
                      <Text>Qty</Text>
                    </View>
                  </View>

                  {props.diabetesOrder.diabetesLineItems.map((li, index) => (
                    <View
                      style={styles.row}
                      key={`diabetes-li-${li.id}-${index}`}
                    >
                      <View style={{ ...styles.infoCell, width: "240px" }}>
                        <Text style={styles.bold}>{li.productName}</Text>
                      </View>
                      <View style={{ ...styles.infoCell, width: "90px" }}>
                        <Text>{li.hcpcsCode ?? ""}</Text>
                      </View>
                      <View style={{ ...styles.infoCell, width: "130px" }}>
                        <Text>{li.size ?? ""}</Text>
                      </View>
                      <View style={{ ...styles.infoCell, width: "80px" }}>
                        <Text>{li.quantity}</Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
            {/* Prosthetics Order Section */}
            {prostheticsSections.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Prosthetics
                </Text>
                <View style={styles.borderBottom} />

                <View style={styles.row}>
                  <View style={styles.flex}>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    {prostheticsDiagnoses.length > 0 ? (
                      <View style={styles.row}>
                        <View style={styles.labelCell}>
                          <Text>Diagnosis</Text>
                        </View>
                        <View style={styles.infoCell}>
                          {prostheticsDiagnoses.map((diag, idx) => (
                            <Text key={`prosthetics-diag-${idx}`}>
                              {idx + 1}.{" "}
                              {[diag.icd10Code, diag.diagnosis]
                                .filter(Boolean)
                                .join(": ")}
                            </Text>
                          ))}
                        </View>
                      </View>
                    ) : null}

                    {optionalInfoRows([
                      [
                        "Functional Level",
                        props.prostheticsOrder?.functionalLevel,
                      ],
                      ["Length of Need", prostheticsLengthOfNeed],
                      [
                        "Preferred DME",
                        props.prostheticsOrder?.preferredDmeCompany,
                      ],
                    ])}
                  </View>
                </View>

                {prostheticsSections.map((section) => (
                  <View
                    key={`prosthetics-${section.heading}`}
                    wrap={
                      section.rows.length + section.lineItems.length >
                      PROSTHETICS_UNBREAKABLE_ROW_LIMIT
                    }
                  >
                    <Text style={prostheticsSectionHeadingStyle}>
                      {section.heading}
                    </Text>

                    {section.rows.length > 0 ? (
                      <>
                        <Text style={prostheticsSubLabelStyle}>
                          Measurements:
                        </Text>
                        <View style={styles.row}>
                          <View style={styles.flex}>{section.rows}</View>
                        </View>
                      </>
                    ) : null}

                    {section.lineItems.length > 0 ? (
                      <>
                        <Text style={prostheticsItemsLabelStyle}>
                          Ordered Items:
                        </Text>
                        <View style={styles.flex}>
                          <View style={styles.row}>
                            {prostheticsColumns(
                              section.capturesItemDetails,
                            ).map((column) => (
                              <View
                                key={column.header}
                                style={{
                                  ...styles.headerCell,
                                  width: column.width,
                                }}
                              >
                                <Text>{column.header}</Text>
                              </View>
                            ))}
                          </View>

                          {section.lineItems.map((li, index) => (
                            <View
                              style={styles.row}
                              key={`prosthetics-li-${li.id}-${index}`}
                            >
                              {prostheticsColumns(
                                section.capturesItemDetails,
                              ).map((column) => (
                                <View
                                  key={column.header}
                                  style={{
                                    ...styles.infoCell,
                                    width: column.width,
                                  }}
                                >
                                  <Text
                                    style={
                                      column.header === "Item"
                                        ? styles.bold
                                        : undefined
                                    }
                                  >
                                    {column.value(li) ?? ""}
                                  </Text>
                                </View>
                              ))}
                            </View>
                          ))}
                        </View>
                      </>
                    ) : null}
                  </View>
                ))}
              </View>
            ) : null}
            {props.careBridgeOrder?.careBridgeLineItems.length ? (
              <View>
                <Text style={styles.sectionHeader}>
                  {props.clinicInfo?.hospitalName.includes("CareBridge")
                    ? "Order Detail: CareBridge"
                    : "Order Detail: Miscellaneous"}
                </Text>
                <View style={styles.borderBottom} />
                {props.careBridgeOrder.additionalDiagnosisInfo && (
                  <>
                    <Text style={styles.subheaderText}>Order Info:</Text>
                    <View style={styles.row}>
                      <View style={styles.labelCell}>
                        <Text>Diagnoses</Text>
                      </View>
                      <View style={styles.infoCell}>
                        <Text>
                          {props.careBridgeOrder.additionalDiagnosisInfo}
                        </Text>
                      </View>
                    </View>
                  </>
                )}
                <Text style={styles.subheaderText}>Ordered Items:</Text>
                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View
                      style={{
                        ...styles.headerCell,
                        ...{ width: "306px !important" },
                      }}
                    >
                      <Text>Product Name</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "170px" }}>
                      <Text>Product Type</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "170px" }}>
                      <Text>Quantity</Text>
                    </View>
                    {props.clinicInfo?.hospitalName.includes(
                      "CareBridge",
                    ) ? null : (
                      <>
                        <View style={{ ...styles.headerCell, width: "170px" }}>
                          <Text>Length of need</Text>
                        </View>
                      </>
                    )}
                  </View>
                  {props.careBridgeOrder.careBridgeLineItems.map(
                    (li, index) => {
                      return (
                        <View key={`${li.id}-${index}`} style={styles.row}>
                          <View
                            style={{
                              ...styles.infoCell,
                              ...{ width: "306px !important" },
                            }}
                          >
                            <Text style={styles.bold}>{li.productName}</Text>
                          </View>
                          <View style={styles.infoCell}>
                            <Text>{li.productType}</Text>
                          </View>
                          <View style={styles.infoCell}>
                            <Text>{li.quantity}</Text>
                          </View>
                          {props.clinicInfo?.hospitalName.includes(
                            "CareBridge",
                          ) ? null : (
                            <>
                              <View
                                style={{ ...styles.infoCell, width: "170px" }}
                              >
                                <Text>
                                  {li.lengthOfNeedLifetime
                                    ? "Lifetime"
                                    : li.lengthOfNeedMonths + " months"}
                                </Text>
                              </View>
                            </>
                          )}
                        </View>
                      );
                    },
                  )}
                </View>
              </View>
            ) : null}
            {/* Miscellaneous Order Section */}
            {props.miscellaneousOrder?.lineItems != null &&
            props.miscellaneousOrder.lineItems.length > 0 ? (
              <View>
                <Text style={styles.sectionHeader}>
                  Order Detail: Miscellaneous
                </Text>

                {props.miscellaneousOrder.diagnoses != null &&
                props.miscellaneousOrder.diagnoses.length > 0 ? (
                  <>
                    <Text style={styles.subheaderText}>Diagnoses:</Text>
                    <View style={styles.flex}>
                      {props.miscellaneousOrder.diagnoses.map(
                        (diagnosis, index) => {
                          return (
                            <View
                              style={styles.row}
                              key={`misc-diagnosis-${index}`}
                            >
                              <View style={styles.labelCell}>
                                <Text>Diagnosis {index + 1}</Text>
                              </View>
                              <View style={styles.infoCell}>
                                <Text>{diagnosis.diagnosis || "N/A"}</Text>
                              </View>
                            </View>
                          );
                        },
                      )}
                    </View>
                  </>
                ) : null}

                <Text style={styles.subheaderText}>Ordered Items:</Text>
                <View style={styles.flex}>
                  <View style={styles.row}>
                    <View style={{ ...styles.headerCell, width: "200px" }}>
                      <Text>Product Name</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "150px" }}>
                      <Text>Size / Specification</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "80px" }}>
                      <Text>Quantity</Text>
                    </View>
                    <View style={{ ...styles.headerCell, width: "216px" }}>
                      <Text>Remarks</Text>
                    </View>
                  </View>
                  {props.miscellaneousOrder.lineItems.map((lineItem, index) => {
                    return (
                      <View
                        style={styles.row}
                        key={`misc-lineitem-${lineItem.id}-${index}`}
                      >
                        <View style={{ ...styles.infoCell, width: "200px" }}>
                          <Text style={styles.bold}>
                            {lineItem.productName}
                          </Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "150px" }}>
                          <Text>{lineItem.sizeSpecification || "N/A"}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "80px" }}>
                          <Text>{lineItem.quantity}</Text>
                        </View>
                        <View style={{ ...styles.infoCell, width: "216px" }}>
                          <Text>{lineItem.remarks || "N/A"}</Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            ) : null}
            {ModernOrderSummary}
            <>
              {!props.careBridgeOrder?.additionalDiagnosisInfo &&
              props.clinicInfo?.departmentType ===
                DepartmentType.CaseManagement ? (
                <Text style={styles.sectionHeader}>
                  Diagnosis & Prescriber Signature
                </Text>
              ) : (
                <Text style={styles.sectionHeader}>Prescriber Signature</Text>
              )}
              {props.signerInfo?.signatureTime ? (
                <Text style={{ ...styles.aboutText, ...styles.marginBottom }}>
                  {`Electronically signed by ${props.signerInfo?.signerName}${
                    props.signerInfo?.signerDegree
                      ? `, ${props.signerInfo?.signerDegree}`
                      : ""
                  } (NPI: ${props.signerInfo?.npiNumber}) - ${formatSignatureTime(
                    props.signerInfo?.signatureTime,
                  )} ${props.signerInfo?.signatureTimezone}`}
                </Text>
              ) : (
                <>
                  {!props.careBridgeOrder?.additionalDiagnosisInfo &&
                    props.clinicInfo?.departmentType ===
                      DepartmentType.CaseManagement && (
                      <View style={styles.row}>
                        <View>
                          <Text style={styles.aboutText}>Diagnosis</Text>
                          <View
                            style={{
                              ...styles.outlineBox,
                              width: "375px",
                              height: "150px",
                            }}
                          />
                        </View>
                      </View>
                    )}
                  <View style={styles.row}>
                    <View>
                      <Text style={styles.aboutText}>Signature</Text>
                      <View style={styles.outlineBox} />
                    </View>
                    <View>
                      <Text style={styles.aboutText}>Date</Text>
                      <View style={styles.outlineBox} />
                    </View>
                  </View>
                  <View style={styles.row}>
                    <View>
                      <Text style={styles.aboutText}>Name (printed)</Text>
                      <View style={styles.outlineBox} />
                    </View>
                    <View>
                      <Text style={styles.aboutText}>NPI</Text>
                      <View style={styles.outlineBox} />
                    </View>
                  </View>
                </>
              )}
              <View style={styles.inline}>
                <Text style={{ ...styles.text }}>
                  {props.clinicInfo?.address1
                    ? `${props.clinicInfo.hospitalName} - ${props.clinicInfo.address1}${props.clinicInfo.address2 ? ` ${props.clinicInfo.address2}` : ""}, ${props.clinicInfo.city}, ${props.clinicInfo.stateAbbr} ${props.clinicInfo.zipcode}`
                    : (props.clinicInfo?.hospitalName ?? "")}
                </Text>
              </View>
            </>
          </View>
        </View>
      </Page>
    </Document>
  );
}
